import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Loader2,
  Download,
  ChevronLeft,
  ChevronRight,
  Users,
  ShoppingCart,
  RefreshCw,
  BarChart3,
  Check,
  ChevronsUpDown,
  Upload,
  FileText,
  X,
  Receipt,
  Send,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { IncomeInvoicesTab } from './IncomeInvoicesTab';
import { AccountingDashboard } from './AccountingDashboard';
import { SortableTableHeader } from './SortableTableHeader';
import { Pagination } from './Pagination';
import { CRMStatsView } from './CRMStatsView';
import { OrdersView } from './OrdersView';
import { CustomersView } from './CustomersView';
import { WritersView } from './WritersView';
import { ExpensesView } from './ExpensesView';
import { IncomeView } from './IncomeView';
import { getSensitiveTopicLabel } from '../../utils/sensitiveTopicsTranslations';
import { isAdLabelYes } from '../../utils/adLabel';
import {
  ORDER_STATUSES,
  PAYMENT_STATUSES,
  STATUS_COLORS,
  PAYMENT_COLORS,
  INCOME_PAYMENT_METHODS,
  INCOME_STATUSES,
  INCOME_STATUS_COLORS,
} from './crmConstants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CRMTab = ({ language, initialSection = null, onPendingCountChange = null }) => {
  const [activeView, setActiveView] = useState(initialSection || 'orders');
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [writers, setWriters] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [stats, setStats] = useState(null);
  const [expenseStats, setExpenseStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  // Full list of orders used by the expense modal combobox (not paginated)
  const [allOrdersForPicker, setAllOrdersForPicker] = useState([]);
  const [expensePagination, setExpensePagination] = useState({ page: 1, total: 0, total_pages: 1 });

  // Lightweight list of ALL expenses' order links (no pagination). Used to detect
  // which orders are already attached to other expenses across the whole system.
  const [allExpenseLinks, setAllExpenseLinks] = useState([]);

  const fetchAllExpenseLinks = React.useCallback(async () => {
    try {
      // Pull all expenses with a high limit; only `id, invoice_number, order_lines` are needed.
      const res = await axios.get(`${API}/admin/expenses`, { params: { page: 1, limit: 10000 } });
      const items = (res.data?.expenses || []).map((e) => ({
        id: e.id,
        invoice_number: e.invoice_number,
        order_lines: e.order_lines || [],
      }));
      setAllExpenseLinks(items);
    } catch (e) {
      // Non-fatal: lock map will just be smaller. Don't toast to avoid noise.
      console.warn('fetchAllExpenseLinks failed', e?.message);
    }
  }, []);

  // Map: order_id -> { expenseId, invoice_number } for ALL orders already attached
  // to ANY expense. Used by the expense modal to disable already-locked orders.
  const lockedOrderIdToExpense = React.useMemo(() => {
    const m = {};
    allExpenseLinks.forEach((exp) => {
      (exp.order_lines || []).forEach((l) => {
        if (l.order_id && !m[l.order_id]) {
          m[l.order_id] = { expenseId: exp.id, invoiceNumber: exp.invoice_number };
        }
      });
    });
    return m;
  }, [allExpenseLinks]);
  
  // Search and filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [expenseSearch, setExpenseSearch] = useState('');
  const [expensePaymentFilter, setExpensePaymentFilter] = useState('');
  
  // Dialog states
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [writerDialogOpen, setWriterDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [orderImportDialogOpen, setOrderImportDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  // For inline expense PDF upload from order modal
  const orderExpenseFileInputRef = React.useRef(null);
  const [orderExpenseFile, setOrderExpenseFile] = useState(null);  // { expense_id, file: { stored_name, original_name } }
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editingWriter, setEditingWriter] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  
  // Order import states
  const [orderImportFile, setOrderImportFile] = useState(null);
  const [orderImportLoading, setOrderImportLoading] = useState(false);
  const [orderImportReplaceAll, setOrderImportReplaceAll] = useState(false);
  const orderImportFileRef = useRef(null);
  
  // Sort
  const [sortField, setSortField] = useState('order_number');
  const [sortOrder, setSortOrder] = useState('desc');
  const [expenseSortField, setExpenseSortField] = useState('created_at');
  const [expenseSortOrder, setExpenseSortOrder] = useState('desc');
  
  // Form states
  const [writerForm, setWriterForm] = useState({ name: '', email: '', active: true });
  
  // Expense form (multi-order)
  const [expenseForm, setExpenseForm] = useState({
    invoice_number: '',
    order_lines: [],  // [{order_id, order_number, pvp, pc, amount}]
    invoice_date: '',
    created_date: new Date().toISOString().split('T')[0],
    payment_date: '',
    provider: '',
    base_amount: 0,
    iva: 0,
    irpf: 0,
    total: 0,
    payment_method: '',
    status: 'No pagada',
    notes: '',
  });
  const [expenseFiles, setExpenseFiles] = useState([]);
  const [pendingExpenseFiles, setPendingExpenseFiles] = useState([]);
  const expenseFileInputRef = React.useRef(null);
  const [orderSelectOpen, setOrderSelectOpen] = useState(false);
  
  // Expense download states
  const [showExpenseDownloadDialog, setShowExpenseDownloadDialog] = useState(false);
  const [expenseDownloadDateFrom, setExpenseDownloadDateFrom] = useState('');
  const [expenseDownloadDateTo, setExpenseDownloadDateTo] = useState('');
  const [expenseDownloadPreset, setExpenseDownloadPreset] = useState('');
  const [expenseDownloadDateField, setExpenseDownloadDateField] = useState('invoice_date');
  
  // Income dialog state
  const [showIncomeDialog, setShowIncomeDialog] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [incomeInvoices, setIncomeInvoices] = useState([]);
  const [incomeFiles, setIncomeFiles] = useState([]);
  const [pendingIncomeFiles, setPendingIncomeFiles] = useState([]);
  const incomeFileInputRef = React.useRef(null);
  const [allCustomers, setAllCustomers] = useState([]);
  const [incomeForm, setIncomeForm] = useState({
    invoice_number: '',
    order_lines: [],
    client_name: '',
    client_nif: '',
    invoice_date: '',
    payment_date: '',
    base_amount: 0,
    iva: 21,
    irpf: 0,
    total: 0,
    payment_method: '',
    status: 'A la espera de cobro',
    description: '',
    notes: ''
  });
  const [showIncomeDownloadDialog, setShowIncomeDownloadDialog] = useState(false);
  const [incomeDownloadDateFrom, setIncomeDownloadDateFrom] = useState('');
  const [incomeDownloadDateTo, setIncomeDownloadDateTo] = useState('');
  const [incomeDownloadPreset, setIncomeDownloadPreset] = useState('');
  const [incomeDownloadDateField, setIncomeDownloadDateField] = useState('invoice_date');
  const [incomeSearch, setIncomeSearch] = useState('');
  const [incomePaymentFilter, setIncomePaymentFilter] = useState('');
  
  // Products for search (from catalog)
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  
  // Combobox open states
  const [customerOpen, setCustomerOpen] = useState(false);
  const [writerOpen, setWriterOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  
  // Selected product details
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // File upload states
  const [uploadingFile, setUploadingFile] = useState(false);
  const [orderFiles, setOrderFiles] = useState([]); // Files already uploaded (for editing)
  const [pendingFiles, setPendingFiles] = useState([]); // Files to upload on save (for new orders)
  const fileInputRef = React.useRef(null);
  
  const [orderForm, setOrderForm] = useState({
    customer_id: '',
    customer_name: '',
    customer_email: '',
    external_order_number: '',
    product_id: '',
    product_url: '',
    web_email: '',
    price_type: 'normal',
    ask_topics: '',
    information: '',
    revision: '',
    published_url: '',
    url_1: '',
    anchor_text_1: '',
    url_2: '',
    anchor_text_2: '',
    writer_instructions: '',
    word_count: '600',
    article_language: '',
    status: 'Contactando',
    writer: '',
    writer_id: '',
    order_date: '',
    final_date: '',
    send_date: '',
    deadline: '',
    pvp: 0,
    pc: 0,
    premium_pvp: 0,
    premium_pc: 0,
    invoice_income_number: '',
    invoice_expense_number: '',
    notified: false,
    payment_status: 'Pendiente',
    collection_date: '',
    invoice_date: '',
    payment_deadline: '',
  });
  
  const [customerForm, setCustomerForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: '',
  });

  const texts = {
    es: {
      orders: 'Pedidos',
      customers: 'Clientes',
      stats: 'Estadísticas',
      newOrder: 'Nuevo Pedido',
      newCustomer: 'Nuevo Cliente',
      search: 'Buscar...',
      orderNumber: 'Nº Pedido',
      customer: 'Cliente',
      productUrl: 'URL Producto',
      status: 'Estado',
      paymentStatus: 'Estado Pago',
      pvp: 'PVP',
      pc: 'PC',
      writer: 'Redactor',
      orderDate: 'F. Pedido',
      actions: 'Acciones',
      save: 'Guardar',
      cancel: 'Cancelar',
      allStatuses: 'Todos los estados',
      allPayments: 'Todos los pagos',
      export: 'Exportar',
      refresh: 'Actualizar',
      totalOrders: 'Total Pedidos',
      totalRevenue: 'Ingresos',
      totalCost: 'Costes',
      margin: 'Margen',
      noOrders: 'No hay pedidos',
      noCustomers: 'No hay clientes',
      customerNumber: 'Nº Cliente',
      externalOrderNumber: 'Nº P Exter',
      email: 'Email',
      phone: 'Teléfono',
      company: 'Empresa',
      name: 'Nombre',
      webEmail: 'Email Web',
      askTopics: 'Preguntar Temáticas',
      information: 'Información',
      revision: 'Revisión',
      publishedUrl: 'URL Publicada',
      finalDate: 'F. Final',
      sendDate: 'F. Envío',
      deadline: 'F. Límite',
      notified: 'Avisado',
      collectionDate: 'F. Cobro',
      invoiceDate: 'F. Factura',
      paymentDeadline: 'F. Límite Pago',
      notes: 'Notas',
      editOrder: 'Editar Pedido',
      editCustomer: 'Editar Cliente',
      page: 'Página',
      of: 'de',
      writers: 'Redactores',
      newWriter: 'Nuevo Redactor',
      editWriter: 'Editar Redactor',
      noWriters: 'No hay redactores',
      active: 'Activo',
      selectWriter: 'Seleccionar redactor',
      selectCustomer: 'Buscar cliente...',
      selectProduct: 'Buscar producto...',
      noCustomerFound: 'No se encontró cliente',
      noWriterFound: 'No se encontró redactor',
      noProductFound: 'No se encontró producto',
      priceType: 'Tipo de Precio',
      normal: 'Normal',
      sensible: 'Temática Sensible',
      invoiceIncome: 'Nº Factura Ingreso',
      invoiceExpense: 'Nº Factura Gasto',
      product: 'Producto',
      salePriceLabel: 'Precio Venta',
      purchasePriceLabel: 'Precio Compra',
      // Expenses/Invoicing
      expenses: 'Facturación',
      newExpense: 'Añadir Gasto',
      editExpense: 'Editar Gasto',
      noExpenses: 'No hay gastos',
      invoiceNumber: 'Nº Factura',
      selectOrder: 'Buscar pedido...',
      noOrderFound: 'No se encontró pedido',
      expenseInvoiceDate: 'Fecha Factura Gasto',
      createdDate: 'Fecha Registro',
      provider: 'Proveedor',
      baseAmount: 'PVP sin IVA',
      iva: 'IVA',
      irpf: 'IRPF',
      total: 'Total',
      paymentMethod: 'Forma de Pago',
      totalExpenses: 'Total Gastos',
      totalBase: 'Total Base',
      viewExpense: 'Ver Factura',
      loadingExpense: 'Cargando factura...',
      noExpenseFound: 'No hay factura asociada',
      expenseStatus: 'Estado',
      statusPaid: 'Pagada',
      statusUnpaid: 'No pagada',
      statusPartial: 'Pagada 50%',
      statusProforma: 'Con proforma',
      requiredFields: 'Campos obligatorios',
      invoiceNumberRequired: 'El Nº Factura es obligatorio',
      orderRequired: 'Debe seleccionar un pedido',
      providerRequired: 'El Proveedor es obligatorio',
      baseAmountRequired: 'El PVP sin IVA es obligatorio',
      statusRequired: 'El Estado es obligatorio',
    },
    en: {
      orders: 'Orders',
      customers: 'Customers',
      stats: 'Statistics',
      newOrder: 'New Order',
      newCustomer: 'New Customer',
      search: 'Search...',
      orderNumber: 'Order #',
      customer: 'Customer',
      productUrl: 'Product URL',
      status: 'Status',
      paymentStatus: 'Payment Status',
      pvp: 'PVP',
      pc: 'PC',
      writer: 'Writer',
      orderDate: 'Order Date',
      actions: 'Actions',
      save: 'Save',
      cancel: 'Cancel',
      allStatuses: 'All statuses',
      allPayments: 'All payments',
      export: 'Export',
      refresh: 'Refresh',
      totalOrders: 'Total Orders',
      totalRevenue: 'Revenue',
      totalCost: 'Costs',
      margin: 'Margin',
      noOrders: 'No orders',
      noCustomers: 'No customers',
      customerNumber: 'Customer #',
      externalOrderNumber: 'External Order #',
      email: 'Email',
      phone: 'Phone',
      company: 'Company',
      name: 'Name',
      webEmail: 'Web Email',
      askTopics: 'Ask Topics',
      information: 'Information',
      revision: 'Revision',
      publishedUrl: 'Published URL',
      finalDate: 'Final Date',
      sendDate: 'Send Date',
      deadline: 'Deadline',
      notified: 'Notified',
      collectionDate: 'Collection Date',
      invoiceDate: 'Invoice Date',
      paymentDeadline: 'Payment Deadline',
      notes: 'Notes',
      editOrder: 'Edit Order',
      editCustomer: 'Edit Customer',
      page: 'Page',
      of: 'of',
      writers: 'Writers',
      newWriter: 'New Writer',
      editWriter: 'Edit Writer',
      noWriters: 'No writers',
      active: 'Active',
      selectWriter: 'Select writer',
      selectCustomer: 'Search customer...',
      selectProduct: 'Search product...',
      noCustomerFound: 'No customer found',
      noWriterFound: 'No writer found',
      noProductFound: 'No product found',
      priceType: 'Price Type',
      normal: 'Normal',
      sensible: 'Sensitive Topic',
      invoiceIncome: 'Income Invoice #',
      invoiceExpense: 'Expense Invoice #',
      product: 'Product',
      salePriceLabel: 'Sale Price',
      purchasePriceLabel: 'Purchase Price',
      // Expenses/Invoicing
      expenses: 'Invoicing',
      newExpense: 'Add Expense',
      editExpense: 'Edit Expense',
      noExpenses: 'No expenses',
      invoiceNumber: 'Invoice #',
      selectOrder: 'Search order...',
      noOrderFound: 'No order found',
      expenseInvoiceDate: 'Expense Invoice Date',
      createdDate: 'Created Date',
      provider: 'Provider',
      baseAmount: 'Amount excl. VAT',
      iva: 'VAT',
      irpf: 'IRPF',
      total: 'Total',
      paymentMethod: 'Payment Method',
      totalExpenses: 'Total Expenses',
      totalBase: 'Total Base',
      viewExpense: 'View Invoice',
      loadingExpense: 'Loading invoice...',
      noExpenseFound: 'No invoice found',
      expenseStatus: 'Status',
      statusPaid: 'Paid',
      statusUnpaid: 'Unpaid',
      statusPartial: '50% Paid',
      statusProforma: 'With Proforma',
      requiredFields: 'Required fields',
      invoiceNumberRequired: 'Invoice # is required',
      orderRequired: 'Must select an order',
      providerRequired: 'Provider is required',
      baseAmountRequired: 'Amount excl. VAT is required',
      statusRequired: 'Status is required',
    }
  };
  
  const t = texts[language] || texts.es;

  const PAYMENT_METHODS = ['Abanca', 'Tarjeta Abanca', 'Wise', 'Paypal'];
  const EXPENSE_STATUSES = ['Pagada', 'No pagada', 'Pagada 50%', 'Con proforma'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchOrders(),
        fetchCustomers(),
        fetchWriters(),
        fetchStats(),
        fetchExpenses(),
        fetchExpenseStats(),
        fetchAllExpenseLinks(),
      ]);
    } catch (error) {
      console.error('Error fetching CRM data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch when filters change (with debounce for search)
  useEffect(() => {
    if (activeView === 'orders') {
      const timer = setTimeout(() => {
        fetchOrders(1);
      }, search ? 300 : 0); // 300ms debounce for search
      return () => clearTimeout(timer);
    }
  }, [statusFilter, paymentFilter, search, activeView]);

  // Auto-fetch expenses when search/filter changes (debounced)
  useEffect(() => {
    if (activeView === 'expenses') {
      const timer = setTimeout(() => {
        fetchExpenses(1);
      }, expenseSearch ? 300 : 0);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseSearch, expensePaymentFilter, activeView]);

  const fetchOrders = async (page = 1) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 50);
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (paymentFilter) params.append('payment_status', paymentFilter);
      params.append('sort_by', sortField);
      params.append('sort_order', sortOrder);
      
      const response = await axios.get(`${API}/admin/orders?${params.toString()}`);
      setOrders(response.data.orders);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchExpenses = async (page = 1) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 50);
      if (expenseSearch) params.append('search', expenseSearch);
      if (expensePaymentFilter && expensePaymentFilter !== 'all') params.append('payment_method', expensePaymentFilter);
      params.append('sort_field', expenseSortField);
      params.append('sort_order', expenseSortOrder);
      
      const response = await axios.get(`${API}/admin/expenses?${params.toString()}`);
      setExpenses(response.data.expenses);
      setExpensePagination({
        page: response.data.page,
        total: response.data.total,
        total_pages: response.data.total_pages
      });
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const fetchExpenseStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/expenses/stats`);
      setExpenseStats(response.data);
    } catch (error) {
      console.error('Error fetching expense stats:', error);
    }
  };

  const fetchCustomers = async (page = 1) => {
    try {
      const response = await axios.get(`${API}/admin/customers?page=${page}&limit=50`);
      setCustomers(response.data.customers);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchWriters = async () => {
    try {
      const response = await axios.get(`${API}/admin/writers`);
      setWriters(response.data.writers);
    } catch (error) {
      console.error('Error fetching writers:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Search products for dropdown
  const searchProducts = async (query) => {
    if (!query || query.length < 1) {
      setProducts([]);
      return;
    }
    setProductSearchLoading(true);
    try {
      const response = await axios.get(`${API}/admin/products/search?q=${encodeURIComponent(query)}&limit=20`);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setProducts([]);
    } finally {
      setProductSearchLoading(false);
    }
  };

  // Handle product selection - auto-fill all prices
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    
    setOrderForm(prev => ({
      ...prev,
      product_id: product.id,
      product_url: product.website,
      web_email: product.web_email || '',
      pvp: product.pvp || 0,
      pc: product.pc || 0,
      premium_pvp: product.premium_sale_price || 0,
      premium_pc: product.premium_purchase_price || 0,
    }));
    setProductOpen(false);
  };

  // Handle customer selection
  const handleCustomerSelect = (customer) => {
    setOrderForm(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_name: customer.name,
      customer_email: customer.email || '',
    }));
    setCustomerOpen(false);
  };

  // Handle writer selection
  const handleWriterSelect = (writer) => {
    setOrderForm(prev => ({
      ...prev,
      writer_id: writer.id,
      writer: writer.name,
    }));
    setWriterOpen(false);
  };

  // File upload handlers
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const allowedExtensions = ['.doc', '.docx', '.pdf', '.txt', '.xls', '.xlsx'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedExtensions.includes(fileExt)) {
      toast.error(language === 'es' 
        ? `Tipo de archivo no permitido. Permitidos: ${allowedExtensions.join(', ')}`
        : `File type not allowed. Allowed: ${allowedExtensions.join(', ')}`
      );
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    
    if (editingOrder) {
      // If editing, upload immediately
      handleFileUpload(file, editingOrder.id);
    } else {
      // If creating new order, add to pending files
      setPendingFiles(prev => [...prev, {
        file: file,
        original_name: file.name,
        size: file.size,
        extension: fileExt
      }]);
      toast.success(language === 'es' ? 'Archivo añadido (se subirá al guardar)' : 'File added (will upload on save)');
    }
    
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileUpload = async (file, orderId) => {
    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/orders/${orderId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(language === 'es' ? 'Archivo subido correctamente' : 'File uploaded successfully');
      setOrderFiles(prev => [...prev, response.data.file]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error uploading file');
    } finally {
      setUploadingFile(false);
    }
  };

  const uploadPendingFiles = async (orderId) => {
    for (const pendingFile of pendingFiles) {
      const formData = new FormData();
      formData.append('file', pendingFile.file);
      try {
        await axios.post(`${API}/admin/orders/${orderId}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
    setPendingFiles([]);
  };

  const handleRemovePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDownloadFile = async (orderId, filename, originalName) => {
    try {
      const response = await axios.get(`${API}/admin/orders/${orderId}/files/${filename}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(language === 'es' ? 'Error descargando archivo' : 'Error downloading file');
    }
  };

  const handleDeleteFile = async (orderId, filename) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar este archivo?' : 'Delete this file?')) return;
    
    try {
      await axios.delete(`${API}/admin/orders/${orderId}/files/${filename}`);
      toast.success(language === 'es' ? 'Archivo eliminado' : 'File deleted');
      setOrderFiles(prev => prev.filter(f => f.filename !== filename));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting file');
    }
  };

  const handleSort = (field) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);
    fetchOrders(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchOrders(1);
  };

  const resetOrderForm = () => {
    setOrderForm({
      customer_id: '',
      customer_name: '',
      customer_email: '',
      external_order_number: '',
      product_id: '',
      product_url: '',
      web_email: '',
      price_type: 'normal',
      ask_topics: '',
      information: '',
      revision: '',
      published_url: '',
      url_1: '',
      anchor_text_1: '',
      url_2: '',
      anchor_text_2: '',
      writer_instructions: '',
      word_count: '600',
      article_language: '',
      status: 'Contactando',
      writer: '',
      writer_id: '',
      order_date: '',
      final_date: '',
      send_date: '',
      deadline: '',
      pvp: 0,
      pc: 0,
      premium_pvp: 0,
      premium_pc: 0,
      invoice_income_number: '',
      invoice_expense_number: '',
      notified: false,
      payment_status: 'Pendiente',
      collection_date: '',
      invoice_date: '',
      payment_deadline: '',
    });
    setSelectedProduct(null);
    setOrderFiles([]);
    setPendingFiles([]);
  };

  const openOrderDialog = async (order = null) => {
    if (order) {
      setEditingOrder(order);
      setOrderFiles(order.attached_files || []);
      setPendingFiles([]);
      // Pre-load any expense invoice file linked to this order
      try {
        const r = await axios.get(`${API}/admin/orders/${order.id}/expense-invoice-file`);
        setOrderExpenseFile(r.data?.file ? { expense_id: r.data.expense_id, file: r.data.file } : null);
      } catch {
        setOrderExpenseFile(null);
      }
      setOrderForm({
        customer_id: order.customer_id || '',
        customer_name: order.customer_name || '',
        customer_email: order.customer_email || '',
        external_order_number: order.external_order_number || '',
        product_id: order.product_id || '',
        product_url: order.product_url || '',
        web_email: order.web_email || '',
        price_type: order.price_type || 'normal',
        ask_topics: order.ask_topics || '',
        information: order.information || '',
        revision: order.revision || '',
        published_url: order.published_url || '',
        url_1: order.url_1 || '',
        anchor_text_1: order.anchor_text_1 || '',
        url_2: order.url_2 || '',
        anchor_text_2: order.anchor_text_2 || '',
        writer_instructions: order.writer_instructions || '',
        word_count: order.word_count || '600',
        article_language: order.article_language || '',
        status: order.status || 'Contactando',
        writer: order.writer || '',
        writer_id: order.writer_id || '',
        order_date: order.order_date ? order.order_date.split('T')[0] : '',
        final_date: order.final_date ? order.final_date.split('T')[0] : '',
        send_date: order.send_date ? order.send_date.split('T')[0] : '',
        deadline: order.deadline ? order.deadline.split('T')[0] : '',
        pvp: order.pvp || 0,
        pc: order.pc || 0,
        premium_pvp: order.premium_pvp || 0,
        premium_pc: order.premium_pc || 0,
        invoice_income_number: order.invoice_income_number || '',
        invoice_expense_number: order.invoice_expense_number || '',
        notified: order.notified || false,
        payment_status: order.payment_status || 'Pendiente',
        collection_date: order.collection_date ? order.collection_date.split('T')[0] : '',
        invoice_date: order.invoice_date ? order.invoice_date.split('T')[0] : '',
        payment_deadline: order.payment_deadline ? order.payment_deadline.split('T')[0] : '',
      });
      setSelectedProduct(null);
      // Load product details (link_type / sensitive / ad_label) for the selected product
      if (order.product_id) {
        try {
          const r = await axios.get(`${API}/admin/products/search?q=${encodeURIComponent(order.product_url || '')}&limit=20`);
          const found = (r.data?.products || []).find(p => p.id === order.product_id);
          if (found) setSelectedProduct(found);
        } catch (e) {
          // Non-critical
        }
      }
    } else {
      setEditingOrder(null);
      setOrderExpenseFile(null);
      resetOrderForm();
    }
    setOrderDialogOpen(true);
  };

  const handleSaveOrder = async () => {
    // Validación: Si estado es "Redactor", obligar a seleccionar un redactor
    if (orderForm.status === 'Redactor' && !orderForm.writer_id) {
      toast.error(language === 'es' ? 'Debes seleccionar un redactor cuando el estado es "Redactor"' : 'You must select a writer when status is "Redactor"');
      return;
    }
    
    // Validación: Si estado es "Redactor", obligar número de palabras e idioma
    if (orderForm.status === 'Redactor') {
      if (!orderForm.word_count) {
        toast.error(language === 'es' ? 'Debes seleccionar el número de palabras' : 'You must select word count');
        return;
      }
      if (!orderForm.article_language) {
        toast.error(language === 'es' ? 'Debes seleccionar el idioma del artículo' : 'You must select article language');
        return;
      }
      // Validación: Al menos una URL con su anchor text
      const hasUrl1 = orderForm.url_1 && orderForm.anchor_text_1;
      const hasUrl2 = orderForm.url_2 && orderForm.anchor_text_2;
      if (!hasUrl1 && !hasUrl2) {
        toast.error(language === 'es' ? 'Debes completar al menos una URL con su Anchor Text' : 'You must complete at least one URL with its Anchor Text');
        return;
      }
    }
    
    // Validación: Si estado es "Publicada", obligar a poner la URL publicada
    if (orderForm.status === 'Publicada' && !orderForm.published_url) {
      toast.error(language === 'es' ? 'Debes introducir la URL publicada cuando el estado es "Publicada"' : 'You must enter the published URL when status is "Published"');
      return;
    }
    
    try {
      const data = { ...orderForm };
      
      // Auto-set order_date to current date for new orders
      if (!editingOrder) {
        data.order_date = new Date().toISOString();
      }
      
      // Convert date strings to ISO format
      ['order_date', 'final_date', 'send_date', 'deadline', 'collection_date', 'invoice_date', 'payment_deadline'].forEach(key => {
        if (data[key] && typeof data[key] === 'string' && !data[key].includes('T')) {
          data[key] = new Date(data[key]).toISOString();
        } else if (!data[key]) {
          delete data[key];
        }
      });

      let savedOrderId = null;
      
      if (editingOrder) {
        await axios.put(`${API}/admin/orders/${editingOrder.id}`, data);
        savedOrderId = editingOrder.id;
        toast.success(language === 'es' ? 'Pedido actualizado' : 'Order updated');
      } else {
        const response = await axios.post(`${API}/admin/orders`, data);
        savedOrderId = response.data.id;
        
        // Upload pending files if any
        if (pendingFiles.length > 0 && savedOrderId) {
          await uploadPendingFiles(savedOrderId);
          toast.success(language === 'es' ? 'Pedido creado con archivos' : 'Order created with files');
        } else {
          toast.success(language === 'es' ? 'Pedido creado' : 'Order created');
        }
      }
      
      // Enviar automáticamente al redactor si el estado es "Redactor" y hay un redactor seleccionado
      if (orderForm.status === 'Redactor' && orderForm.writer_id && savedOrderId) {
        try {
          // Find writer details
          const selectedWriter = writers.find(w => w.id === orderForm.writer_id);
          if (selectedWriter) {
            await axios.post(`${API}/admin/orders/${savedOrderId}/send-to-writer`, {
              writer_email: selectedWriter.email,
              writer_name: selectedWriter.name,
              article_instructions: orderForm.writer_instructions || '',
              word_count: orderForm.word_count,
              article_language: orderForm.article_language
            });
            toast.success(language === 'es' ? 'Enviado al redactor automáticamente' : 'Automatically sent to writer');
          }
        } catch (sendError) {
          console.error('Error sending to writer:', sendError);
          toast.warning(language === 'es' ? 'Pedido guardado, pero error al enviar al redactor' : 'Order saved, but error sending to writer');
        }
      }
      
      setOrderDialogOpen(false);
      fetchOrders();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving order');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar este pedido?' : 'Delete this order?')) return;
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`);
      toast.success(language === 'es' ? 'Pedido eliminado' : 'Order deleted');
      fetchOrders();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting order');
    }
  };

  const handleSendToWriter = async (orderId) => {
    try {
      // Find the order to get writer info
      const order = orders.find(o => o.id === orderId);
      if (!order) {
        toast.error(language === 'es' ? 'Pedido no encontrado' : 'Order not found');
        return;
      }
      
      // Find the writer by ID
      const writer = writers.find(w => w.id === order.writer_id);
      if (!writer) {
        toast.error(language === 'es' ? 'No hay redactor asignado a este pedido' : 'No writer assigned to this order');
        return;
      }
      
      const response = await axios.post(`${API}/admin/orders/${orderId}/send-to-writer`, {
        writer_email: writer.email,
        writer_name: writer.name,
        article_instructions: order.writer_instructions || ''
      });
      toast.success(response.data.message || (language === 'es' ? 'Enviado al redactor' : 'Sent to writer'));
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al enviar' : 'Error sending'));
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}/status`, { status: newStatus });
      toast.success(language === 'es' ? 'Estado actualizado' : 'Status updated');
      fetchOrders();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating status');
    }
  };

  const handleUpdateOrderStatus = async (orderId, field, value) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}`, { [field]: value });
      toast.success(language === 'es' ? 'Actualizado' : 'Updated');
      fetchOrders();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating');
    }
  };

  const handleExportOrders = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (paymentFilter) params.append('payment_status', paymentFilter);
      
      const response = await axios.get(`${API}/admin/orders/export?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `pedidos_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      toast.success(language === 'es' ? 'Exportación completada' : 'Export completed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error exporting');
    }
  };

  // Order import function
  const handleImportOrders = async () => {
    if (!orderImportFile) {
      toast.error(language === 'es' ? 'Selecciona un archivo' : 'Select a file');
      return;
    }
    
    setOrderImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', orderImportFile);
      
      const response = await axios.post(
        `${API}/admin/orders/upload?replace_all=${orderImportReplaceAll}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      const { imported, errors, error_details } = response.data;
      toast.success(
        language === 'es' 
          ? `Importados ${imported} pedidos${errors > 0 ? `, ${errors} errores` : ''}`
          : `Imported ${imported} orders${errors > 0 ? `, ${errors} errors` : ''}`
      );
      
      if (error_details && error_details.length > 0) {
        console.log('Import errors:', error_details);
      }
      
      setOrderImportDialogOpen(false);
      setOrderImportFile(null);
      setOrderImportReplaceAll(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error importing orders');
    } finally {
      setOrderImportLoading(false);
    }
  };

  // Customer functions
  const resetCustomerForm = () => {
    setCustomerForm({ name: '', email: '', phone: '', company: '', nif: '', billing_address: '', notes: '' });
  };

  const openCustomerDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setCustomerForm({
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        company: customer.company || '',
        nif: customer.nif || '',
        billing_address: customer.billing_address || '',
        notes: customer.notes || '',
      });
    } else {
      setEditingCustomer(null);
      resetCustomerForm();
    }
    setCustomerDialogOpen(true);
  };

  const handleSaveCustomer = async () => {
    try {
      if (editingCustomer) {
        await axios.put(`${API}/admin/customers/${editingCustomer.id}`, customerForm);
        toast.success(language === 'es' ? 'Cliente actualizado' : 'Customer updated');
      } else {
        await axios.post(`${API}/admin/customers`, customerForm);
        toast.success(language === 'es' ? 'Cliente creado' : 'Customer created');
      }
      setCustomerDialogOpen(false);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving customer');
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar este cliente?' : 'Delete this customer?')) return;
    try {
      await axios.delete(`${API}/admin/customers/${customerId}`);
      toast.success(language === 'es' ? 'Cliente eliminado' : 'Customer deleted');
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting customer');
    }
  };

  const handleApproveCustomer = async (customerId) => {
    try {
      await axios.put(`${API}/admin/customers/${customerId}/approve`);
      toast.success(language === 'es' ? 'Cliente aprobado correctamente' : 'Customer approved successfully');
      fetchCustomers();
      // Refresh pending count in parent component
      if (onPendingCountChange) {
        onPendingCountChange();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al aprobar cliente' : 'Error approving customer'));
    }
  };

  // Writer functions
  const resetWriterForm = () => {
    setWriterForm({ name: '', email: '', active: true });
  };

  const openWriterDialog = (writer = null) => {
    if (writer) {
      setEditingWriter(writer);
      setWriterForm({
        name: writer.name || '',
        email: writer.email || '',
        active: writer.active !== false,
      });
    } else {
      setEditingWriter(null);
      resetWriterForm();
    }
    setWriterDialogOpen(true);
  };

  const handleSaveWriter = async () => {
    try {
      if (editingWriter) {
        await axios.put(`${API}/admin/writers/${editingWriter.id}`, writerForm);
        toast.success(language === 'es' ? 'Redactor actualizado' : 'Writer updated');
      } else {
        await axios.post(`${API}/admin/writers`, writerForm);
        toast.success(language === 'es' ? 'Redactor creado' : 'Writer created');
      }
      setWriterDialogOpen(false);
      fetchWriters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving writer');
    }
  };

  const handleDeleteWriter = async (writerId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar este redactor?' : 'Delete this writer?')) return;
    try {
      await axios.delete(`${API}/admin/writers/${writerId}`);
      toast.success(language === 'es' ? 'Redactor eliminado' : 'Writer deleted');
      fetchWriters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting writer');
    }
  };

  // ==================== EXPENSE FUNCTIONS ====================
  
  const resetExpenseForm = () => {
    setExpenseForm({
      invoice_number: '',
      order_lines: [],
      invoice_date: '',
      created_date: new Date().toISOString().split('T')[0],
      payment_date: '',
      provider: '',
      base_amount: 0,
      iva: 0,
      irpf: 0,
      total: 0,
      payment_method: '',
      status: 'No pagada',
      notes: '',
    });
    setExpenseFiles([]);
    setPendingExpenseFiles([]);
  };

  const openExpenseDialog = async (expense = null, prefillOrder = null) => {
    // Always load the full list of orders for the combobox so search works across all pages
    // Also refresh the lock map so newly created expenses block previously-free orders.
    try {
      const [resOrders] = await Promise.all([
        axios.get(`${API}/admin/orders?page=1&limit=10000&sort_by=order_number&sort_order=desc`),
        fetchAllExpenseLinks(),
      ]);
      setAllOrdersForPicker(resOrders.data.orders || []);
    } catch (err) {
      console.error('Error fetching orders for picker:', err);
      setAllOrdersForPicker(orders);
    }
    if (expense) {
      setEditingExpense(expense);
      setExpenseFiles(expense.attached_files || []);
      setPendingExpenseFiles([]);
      setExpenseFormErrors({});
      // Build order_lines: prefer multi-line; fallback to legacy single-order shape
      let lines = expense.order_lines || [];
      if ((!lines || lines.length === 0) && expense.order_id) {
        lines = [{
          order_id: expense.order_id,
          order_number: expense.order_number || 0,
          pvp: expense.order_pvp || 0,
          pc: expense.order_pc || 0,
          amount: expense.total || 0,
        }];
      }
      setExpenseForm({
        invoice_number: expense.invoice_number || '',
        order_lines: lines,
        invoice_date: expense.invoice_date ? expense.invoice_date.split('T')[0] : '',
        created_date: expense.created_date ? expense.created_date.split('T')[0] : '',
        payment_date: expense.payment_date ? expense.payment_date.split('T')[0] : '',
        provider: expense.provider || '',
        base_amount: expense.base_amount || 0,
        iva: expense.iva || 0,
        irpf: expense.irpf || 0,
        total: expense.total || 0,
        payment_method: expense.payment_method || '',
        status: expense.status || 'No pagada',
        notes: expense.notes || '',
      });
    } else {
      setEditingExpense(null);
      resetExpenseForm();
      setExpenseFormErrors({});
      // If we're opening from a specific order, prefill the first line
      if (prefillOrder) {
        // We need to set the form AFTER reset; use a microtask via setTimeout 0
        setTimeout(() => handleAddOrderLineForExpense(prefillOrder), 0);
      }
    }
    setExpenseDialogOpen(true);
  };

  const viewOrderExpense = async (orderId) => {
    try {
      const fileRes = await axios.get(`${API}/admin/orders/${orderId}/expense-invoice-file`);
      if (fileRes.data?.file?.stored_name && fileRes.data?.expense_id) {
        // Fetch as blob with auth, then open in new tab
        try {
          const fileUrl = `${API}/admin/expenses/${fileRes.data.expense_id}/files/${fileRes.data.file.stored_name}`;
          const blobResp = await axios.get(fileUrl, { responseType: 'blob' });
          const blobUrl = window.URL.createObjectURL(new Blob([blobResp.data], { type: blobResp.headers['content-type'] || 'application/octet-stream' }));
          const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
          if (!win) {
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = fileRes.data.file.original_name || 'factura';
            document.body.appendChild(a);
            a.click();
            a.remove();
          }
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
          return;
        } catch {
          // Fall through to modal as fallback
        }
      }
      // No file attached — fall back to the expense modal (edit existing or create new)
      const response = await axios.get(`${API}/admin/expenses/by-order/${orderId}`);
      const expense = response.data;
      if (expense && expense.id) {
        openExpenseDialog(expense);
        setActiveView('expenses');
      } else {
        const order = orders.find(o => o.id === orderId) || allOrdersForPicker.find(o => o.id === orderId);
        if (order) {
          openExpenseDialog(null, order);
          setActiveView('expenses');
        } else {
          toast.error(t.noExpenseFound);
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(t.noExpenseFound);
      } else {
        toast.error(error.response?.data?.detail || 'Error loading expense');
      }
    }
  };

  // Add an order as a new line in the expense
  const handleAddOrderLineForExpense = (order) => {
    if (!order) return;
    setExpenseForm(prev => {
      // Skip if order already in lines
      if (prev.order_lines.some(l => l.order_id === order.id)) {
        toast.info(language === 'es' ? 'Pedido ya añadido' : 'Order already added');
        return prev;
      }
      const pc = parseFloat(order.pc) || 0;
      const newLine = {
        order_id: order.id,
        order_number: order.order_number,
        pvp: parseFloat(order.pvp) || 0,
        pc: pc,
        amount: pc,
      };
      const newLines = [...prev.order_lines, newLine];
      // Auto-fill provider/total/invoice_date if first line
      let providerGuess = prev.provider;
      if (!prev.provider?.trim() && order.product_url) {
        try {
          const url = order.product_url.startsWith('http') ? order.product_url : `https://${order.product_url}`;
          providerGuess = new URL(url).hostname.replace(/^www\./, '');
        } catch {
          providerGuess = order.product_url;
        }
      }
      const newTotal = newLines.reduce((sum, l) => sum + (parseFloat(l.amount) || 0), 0);
      return {
        ...prev,
        order_lines: newLines,
        provider: providerGuess,
        base_amount: prev.base_amount ? prev.base_amount : newTotal,
        total: prev.total ? prev.total : newTotal,
        invoice_date: prev.invoice_date ? prev.invoice_date : new Date().toISOString().split('T')[0],
      };
    });
    setOrderSelectOpen(false);
  };

  const handleRemoveOrderLineForExpense = (orderId) => {
    setExpenseForm(prev => ({
      ...prev,
      order_lines: prev.order_lines.filter(l => l.order_id !== orderId),
    }));
  };

  const handleUpdateOrderLineAmount = (orderId, newAmount) => {
    setExpenseForm(prev => ({
      ...prev,
      order_lines: prev.order_lines.map(l =>
        l.order_id === orderId ? { ...l, amount: parseFloat(newAmount) || 0 } : l
      ),
    }));
  };

  // Backwards compat shim — old combobox onSelect handler
  const handleOrderSelectForExpense = (order) => {
    handleAddOrderLineForExpense(order);
  };

  // Per-field expense form errors for inline display
  const [expenseFormErrors, setExpenseFormErrors] = useState({});

  const handleSaveExpense = async () => {
    // Validate required fields with per-field error markers
    const fieldErrors = {};
    if (!expenseForm.invoice_number?.trim()) fieldErrors.invoice_number = t.invoiceNumberRequired;
    if (!expenseForm.provider?.trim())       fieldErrors.provider       = t.providerRequired;
    if (!expenseForm.base_amount || expenseForm.base_amount <= 0) fieldErrors.base_amount = t.baseAmountRequired;
    if (!expenseForm.status)                 fieldErrors.status         = t.statusRequired;

    setExpenseFormErrors(fieldErrors);

    if (Object.keys(fieldErrors).length > 0) {
      toast.error(
        `${t.requiredFields}: ${Object.values(fieldErrors).join(' · ')}`,
        { duration: 6000 }
      );
      // Scroll first invalid field into view
      const firstInvalid = document.querySelector('[data-expense-field-error="true"]');
      if (firstInvalid?.scrollIntoView) firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Soft warn if line sum doesn't match invoice total (when there are lines)
    if (expenseForm.order_lines.length > 0) {
      const linesSum = expenseForm.order_lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
      const invoiceTotal = parseFloat(expenseForm.total) || 0;
      if (Math.abs(linesSum - invoiceTotal) > 0.01) {
        toast.warning(
          language === 'es'
            ? `La suma de las líneas (€${linesSum.toFixed(2)}) no coincide con el total (€${invoiceTotal.toFixed(2)})`
            : `Lines sum (€${linesSum.toFixed(2)}) does not match total (€${invoiceTotal.toFixed(2)})`
        );
      }
    }

    try {
      const data = {
        ...expenseForm,
        order_lines: expenseForm.order_lines.map(l => ({
          order_id: l.order_id,
          order_number: parseInt(l.order_number) || 0,
          pvp: parseFloat(l.pvp) || 0,
          pc: parseFloat(l.pc) || 0,
          amount: parseFloat(l.amount) || 0,
        })),
      };
      if (data.invoice_date) {
        data.invoice_date = new Date(data.invoice_date).toISOString();
      } else {
        delete data.invoice_date;
      }
      if (data.created_date) {
        data.created_date = new Date(data.created_date).toISOString();
      } else {
        delete data.created_date;
      }
      if (data.payment_date) {
        data.payment_date = new Date(data.payment_date).toISOString();
      } else {
        delete data.payment_date;
      }

      if (editingExpense) {
        await axios.put(`${API}/admin/expenses/${editingExpense.id}`, data);
        toast.success(language === 'es' ? 'Gasto actualizado' : 'Expense updated');
      } else {
        const response = await axios.post(`${API}/admin/expenses`, data);
        const newExpenseId = response.data.id;

        if (pendingExpenseFiles.length > 0 && newExpenseId) {
          for (const pf of pendingExpenseFiles) {
            const formData = new FormData();
            formData.append('file', pf.file);
            await axios.post(`${API}/admin/expenses/${newExpenseId}/upload`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          }
          toast.success(language === 'es' ? 'Gasto creado con archivos' : 'Expense created with files');
        } else {
          toast.success(language === 'es' ? 'Gasto creado' : 'Expense created');
        }
      }
      setExpenseDialogOpen(false);
      fetchExpenses();
      fetchExpenseStats();
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving expense');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar este gasto?' : 'Delete this expense?')) return;
    try {
      await axios.delete(`${API}/admin/expenses/${expenseId}`);
      toast.success(language === 'es' ? 'Gasto eliminado' : 'Expense deleted');
      fetchExpenses();
      fetchExpenseStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting expense');
    }
  };

  // Expense file handlers
  const handleExpenseFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const allowedExtensions = ['.doc', '.docx', '.pdf', '.txt', '.xls', '.xlsx', '.jpg', '.jpeg', '.png'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedExtensions.includes(fileExt)) {
      toast.error(language === 'es' 
        ? `Tipo de archivo no permitido`
        : `File type not allowed`
      );
      if (expenseFileInputRef.current) expenseFileInputRef.current.value = '';
      return;
    }
    
    if (editingExpense) {
      handleExpenseFileUpload(file, editingExpense.id);
    } else {
      setPendingExpenseFiles(prev => [...prev, {
        file: file,
        original_name: file.name,
        size: file.size,
        extension: fileExt
      }]);
      toast.success(language === 'es' ? 'Archivo añadido' : 'File added');
    }
    
    if (expenseFileInputRef.current) expenseFileInputRef.current.value = '';
  };

  const handleExpenseFileUpload = async (file, expenseId) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/expenses/${expenseId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(language === 'es' ? 'Archivo subido' : 'File uploaded');
      setExpenseFiles(prev => [...prev, response.data.file]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error uploading file');
    }
  };

  const handleDownloadExpenseFile = async (expenseId, filename, originalName) => {
    try {
      const response = await axios.get(`${API}/admin/expenses/${expenseId}/files/${filename}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(language === 'es' ? 'Error descargando archivo' : 'Error downloading file');
    }
  };

  const handleDeleteExpenseFile = async (expenseId, filename) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar archivo?' : 'Delete file?')) return;
    try {
      await axios.delete(`${API}/admin/expenses/${expenseId}/files/${filename}`);
      toast.success(language === 'es' ? 'Archivo eliminado' : 'File deleted');
      setExpenseFiles(prev => prev.filter(f => f.filename !== filename));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting file');
    }
  };

  const handleRemovePendingExpenseFile = (index) => {
    setPendingExpenseFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Download all expenses
  const handleDownloadExpenses = async () => {
    try {
      const params = { date_field: expenseDownloadDateField };
      
      if (expenseDownloadPreset === 'last_month') {
        params.preset = 'last_month';
      } else {
        if (expenseDownloadDateFrom) params.date_from = expenseDownloadDateFrom;
        if (expenseDownloadDateTo) params.date_to = expenseDownloadDateTo;
      }
      
      const response = await axios.get(`${API}/admin/expenses/download`, {
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gastos_${expenseDownloadDateFrom || 'all'}_${expenseDownloadDateTo || 'all'}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setShowExpenseDownloadDialog(false);
      toast.success(language === 'es' ? 'Descarga iniciada' : 'Download started');
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error(language === 'es' ? 'Error al descargar' : 'Download error');
    }
  };

  // Income invoices functions
  const fetchIncomeInvoices = async () => {
    try {
      const response = await axios.get(`${API}/admin/income-invoices`, {
        params: { limit: 100 }
      });
      setIncomeInvoices(response.data.invoices);
    } catch (error) {
      console.error('Error fetching income invoices:', error);
    }
  };

  const fetchAllCustomers = async () => {
    try {
      const response = await axios.get(`${API}/admin/customers`, {
        params: { limit: 500 }
      });
      console.log('Customers loaded:', response.data.customers?.length || 0);
      setAllCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleIncomeCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setIncomeForm(prev => ({
        ...prev,
        client_name: customer.company || customer.name,
        client_nif: customer.nif || ''
      }));
    }
  };

  const openIncomeDialog = async (income = null, prefillOrder = null) => {
    // Fetch customers and orders when opening dialog
    fetchAllCustomers();
    try {
      const res = await axios.get(`${API}/admin/orders?page=1&limit=10000&sort_by=order_number&sort_order=desc`);
      setAllOrdersForPicker(res.data.orders || []);
    } catch {
      setAllOrdersForPicker(orders);
    }

    if (income) {
      setEditingIncome(income);
      setIncomeFiles(income.attached_files || []);
      setPendingIncomeFiles([]);
      let lines = income.order_lines || [];
      if ((!lines || lines.length === 0) && income.order_id) {
        lines = [{
          order_id: income.order_id,
          order_number: income.order_number || 0,
          pvp: 0, pc: 0,
          amount: income.total || 0,
        }];
      }
      setIncomeForm({
        invoice_number: income.invoice_number || '',
        order_lines: lines,
        client_name: income.client_name || '',
        client_nif: income.client_nif || '',
        invoice_date: income.invoice_date ? income.invoice_date.split('T')[0] : '',
        payment_date: income.payment_date ? income.payment_date.split('T')[0] : '',
        base_amount: income.base_amount || 0,
        iva: income.iva || 21,
        irpf: income.irpf || 0,
        total: income.total || 0,
        payment_method: income.payment_method || '',
        status: income.status || 'A la espera de cobro',
        description: income.description || '',
        notes: income.notes || ''
      });
    } else {
      setEditingIncome(null);
      setIncomeFiles([]);
      setPendingIncomeFiles([]);
      setIncomeForm({
        invoice_number: '',
        order_lines: [],
        client_name: '',
        client_nif: '',
        invoice_date: new Date().toISOString().split('T')[0],
        payment_date: '',
        base_amount: 0,
        iva: 21,
        irpf: 0,
        total: 0,
        payment_method: '',
        status: 'A la espera de cobro',
        description: '',
        notes: ''
      });
      if (prefillOrder) {
        setTimeout(() => handleAddOrderLineForIncome(prefillOrder), 0);
      }
    }
    setShowIncomeDialog(true);
  };

  const handleAddOrderLineForIncome = (order) => {
    if (!order) return;
    setIncomeForm(prev => {
      if (prev.order_lines.some(l => l.order_id === order.id)) {
        toast.info(language === 'es' ? 'Pedido ya añadido' : 'Order already added');
        return prev;
      }
      const pvp = parseFloat(order.pvp) || 0;
      const newLine = {
        order_id: order.id,
        order_number: order.order_number,
        pvp: pvp,
        pc: parseFloat(order.pc) || 0,
        amount: pvp,
      };
      const newLines = [...prev.order_lines, newLine];
      const newSum = newLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
      return {
        ...prev,
        order_lines: newLines,
        client_name: prev.client_name?.trim() ? prev.client_name : (order.customer_name || ''),
        base_amount: prev.base_amount ? prev.base_amount : newSum,
        total: prev.total ? prev.total : newSum,
      };
    });
  };

  const handleRemoveOrderLineForIncome = (orderId) => {
    setIncomeForm(prev => ({
      ...prev,
      order_lines: prev.order_lines.filter(l => l.order_id !== orderId),
    }));
  };

  const handleUpdateIncomeLineAmount = (orderId, newAmount) => {
    setIncomeForm(prev => ({
      ...prev,
      order_lines: prev.order_lines.map(l =>
        l.order_id === orderId ? { ...l, amount: parseFloat(newAmount) || 0 } : l
      ),
    }));
  };

  const handleSaveIncome = async () => {
    try {
      const data = {
        ...incomeForm,
        order_lines: (incomeForm.order_lines || []).map(l => ({
          order_id: l.order_id,
          order_number: parseInt(l.order_number) || 0,
          pvp: parseFloat(l.pvp) || 0,
          pc: parseFloat(l.pc) || 0,
          amount: parseFloat(l.amount) || 0,
        })),
        invoice_date: incomeForm.invoice_date ? new Date(incomeForm.invoice_date).toISOString() : null,
        payment_date: incomeForm.payment_date ? new Date(incomeForm.payment_date).toISOString() : null,
      };

      let incomeId;
      if (editingIncome) {
        await axios.put(`${API}/admin/income-invoices/${editingIncome.id}`, data);
        incomeId = editingIncome.id;
        toast.success(language === 'es' ? 'Factura actualizada' : 'Invoice updated');
      } else {
        const response = await axios.post(`${API}/admin/income-invoices`, data);
        incomeId = response.data.id;
        toast.success(language === 'es' ? 'Factura creada' : 'Invoice created');
      }
      
      // Upload pending files
      for (const file of pendingIncomeFiles) {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/admin/income-invoices/${incomeId}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      setShowIncomeDialog(false);
      setPendingIncomeFiles([]);
      fetchIncomeInvoices();
    } catch (error) {
      console.error('Error saving income:', error);
      toast.error(language === 'es' ? 'Error al guardar' : 'Error saving');
    }
  };

  const handleIncomeFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setPendingIncomeFiles(prev => [...prev, ...files]);
    if (incomeFileInputRef.current) incomeFileInputRef.current.value = '';
  };

  const handleRemovePendingIncomeFile = (index) => {
    setPendingIncomeFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteIncomeFile = async (incomeId, filename) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar archivo?' : 'Delete file?')) return;
    try {
      await axios.delete(`${API}/admin/income-invoices/${incomeId}/files/${filename}`);
      toast.success(language === 'es' ? 'Archivo eliminado' : 'File deleted');
      setIncomeFiles(prev => prev.filter(f => f.filename !== filename));
    } catch (error) {
      toast.error(language === 'es' ? 'Error al eliminar archivo' : 'Error deleting file');
    }
  };

  const handleDownloadIncomeFile = async (incomeId, filename, originalName) => {
    try {
      const response = await axios.get(`${API}/admin/income-invoices/${incomeId}/files/${filename}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName || filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error(language === 'es' ? 'Error al descargar' : 'Download error');
    }
  };

  const handleDeleteIncome = async (id) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar factura?' : 'Delete invoice?')) return;
    try {
      await axios.delete(`${API}/admin/income-invoices/${id}`);
      toast.success(language === 'es' ? 'Factura eliminada' : 'Invoice deleted');
      fetchIncomeInvoices();
    } catch (error) {
      console.error('Error deleting income:', error);
      toast.error(language === 'es' ? 'Error al eliminar' : 'Error deleting');
    }
  };

  const handleDownloadIncome = async () => {
    try {
      const params = { date_field: incomeDownloadDateField };
      if (incomeDownloadPreset === 'last_month') {
        params.preset = 'last_month';
      } else {
        if (incomeDownloadDateFrom) params.date_from = incomeDownloadDateFrom;
        if (incomeDownloadDateTo) params.date_to = incomeDownloadDateTo;
      }
      
      const response = await axios.get(`${API}/admin/income-invoices/download`, {
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ingresos_${incomeDownloadDateFrom || 'all'}_${incomeDownloadDateTo || 'all'}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setShowIncomeDownloadDialog(false);
      toast.success(language === 'es' ? 'Descarga iniciada' : 'Download started');
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error(language === 'es' ? 'Error al descargar' : 'Download error');
    }
  };

  // Calculate income total when amounts change
  React.useEffect(() => {
    const base = parseFloat(incomeForm.base_amount) || 0;
    const ivaAmount = base * (parseFloat(incomeForm.iva) || 0) / 100;
    const irpfAmount = base * (parseFloat(incomeForm.irpf) || 0) / 100;
    const total = base + ivaAmount - irpfAmount;
    setIncomeForm(prev => ({ ...prev, total: Math.round(total * 100) / 100 }));
  }, [incomeForm.base_amount, incomeForm.iva, incomeForm.irpf]);

  // Fetch income invoices on mount
  React.useEffect(() => {
    fetchIncomeInvoices();
    fetchAllCustomers();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* View Tabs - Only show when not in single-section mode */}
      {!initialSection && (
        <div className="flex flex-wrap gap-2 p-1 bg-slate-100 rounded-xl w-fit">
          <Button
            variant={activeView === 'orders' ? 'default' : 'ghost'}
            onClick={() => setActiveView('orders')}
            className={activeView === 'orders' ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md' : 'text-slate-600 hover:text-indigo-600 hover:bg-white'}
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            {t.orders}
          </Button>
          <Button
            variant={activeView === 'customers' ? 'default' : 'ghost'}
            onClick={() => setActiveView('customers')}
            className={activeView === 'customers' ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md' : 'text-slate-600 hover:text-emerald-600 hover:bg-white'}
          >
            <Users className="h-4 w-4 mr-2" />
            {t.customers}
          </Button>
          <Button
            variant={activeView === 'writers' ? 'default' : 'ghost'}
            onClick={() => setActiveView('writers')}
            className={activeView === 'writers' ? 'bg-amber-600 hover:bg-amber-700 text-white shadow-md' : 'text-slate-600 hover:text-amber-600 hover:bg-white'}
          >
            <Pencil className="h-4 w-4 mr-2" />
            {t.writers}
          </Button>
          <Button
            variant={activeView === 'expenses' ? 'default' : 'ghost'}
            onClick={() => setActiveView('expenses')}
            className={activeView === 'expenses' ? 'bg-rose-600 hover:bg-rose-700 text-white shadow-md' : 'text-slate-600 hover:text-rose-600 hover:bg-white'}
          >
            <FileText className="h-4 w-4 mr-2" />
            {t.expenses}
          </Button>
          <Button
            variant={activeView === 'stats' ? 'default' : 'ghost'}
            onClick={() => setActiveView('stats')}
            className={activeView === 'stats' ? 'bg-violet-600 hover:bg-violet-700 text-white shadow-md' : 'text-slate-600 hover:text-violet-600 hover:bg-white'}
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            {t.stats}
          </Button>
          <Button
            variant={activeView === 'accounting' ? 'default' : 'ghost'}
            onClick={() => setActiveView('accounting')}
            className={activeView === 'accounting' ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md' : 'text-slate-600 hover:text-blue-600 hover:bg-white'}
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            {language === 'es' ? 'Contabilidad' : 'Accounting'}
          </Button>
        </div>
      )}

      {/* Stats View */}
      {activeView === 'stats' && stats && (
        <CRMStatsView stats={stats} t={t} />
      )}

      {/* Accounting Dashboard View */}
      {activeView === 'accounting' && (
        <AccountingDashboard language={language} />
      )}

      {/* Orders View */}
      {activeView === 'orders' && (
        <OrdersView
          orders={orders}
          pagination={pagination}
          search={search}
          setSearch={setSearch}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          paymentFilter={paymentFilter}
          setPaymentFilter={setPaymentFilter}
          sortField={sortField}
          sortOrder={sortOrder}
          handleSort={handleSort}
          handleSearch={handleSearch}
          fetchOrders={fetchOrders}
          openOrderDialog={openOrderDialog}
          handleDeleteOrder={handleDeleteOrder}
          handleStatusChange={handleStatusChange}
          handleUpdateOrderStatus={handleUpdateOrderStatus}
          viewOrderExpense={viewOrderExpense}
          handleExportOrders={handleExportOrders}
          setOrderImportDialogOpen={setOrderImportDialogOpen}
          language={language}
          t={t}
        />
      )}

      {/* Customers View */}
      {activeView === 'customers' && (
        <CustomersView
          customers={customers}
          openCustomerDialog={openCustomerDialog}
          handleDeleteCustomer={handleDeleteCustomer}
          handleApproveCustomer={handleApproveCustomer}
          language={language}
          t={t}
        />
      )}

      {/* Writers View */}
      {activeView === 'writers' && (
        <WritersView
          writers={writers}
          openWriterDialog={openWriterDialog}
          handleDeleteWriter={handleDeleteWriter}
          t={t}
        />
      )}

      {/* INCOME VIEW (dedicated) */}
      {activeView === 'income' && (() => {
        const lowerSearch = (incomeSearch || '').toLowerCase();
        const filtered = (incomeInvoices || []).filter((inv) => {
          if (incomePaymentFilter && inv.status !== incomePaymentFilter) return false;
          if (!lowerSearch) return true;
          return (
            (inv.invoice_number || '').toLowerCase().includes(lowerSearch) ||
            (inv.client_name || '').toLowerCase().includes(lowerSearch) ||
            (inv.client_nif || '').toLowerCase().includes(lowerSearch) ||
            String(inv.order_number || '').includes(lowerSearch)
          );
        });
        const stats = filtered.reduce(
          (acc, inv) => {
            const total = parseFloat(inv.total) || 0;
            if (inv.status === 'Cobrada' || inv.status === 'Paid') acc.total_paid += total;
            else acc.total_pending += total;
            return acc;
          },
          { total_paid: 0, total_pending: 0 }
        );
        return (
          <IncomeView
            incomeInvoices={filtered}
            incomeStats={stats}
            incomePagination={null}
            incomeSearch={incomeSearch}
            setIncomeSearch={setIncomeSearch}
            incomePaymentFilter={incomePaymentFilter}
            setIncomePaymentFilter={setIncomePaymentFilter}
            fetchIncomeInvoices={fetchIncomeInvoices}
            openIncomeDialog={openIncomeDialog}
            handleDeleteIncome={handleDeleteIncome}
            setShowIncomeDownloadDialog={setShowIncomeDownloadDialog}
            language={language}
            t={t}
          />
        );
      })()}

      {/* EXPENSES VIEW */}
      {activeView === 'expenses' && (
        <ExpensesView
          expenses={expenses}
          expenseStats={expenseStats}
          expensePagination={expensePagination}
          expenseSearch={expenseSearch}
          setExpenseSearch={setExpenseSearch}
          expensePaymentFilter={expensePaymentFilter}
          setExpensePaymentFilter={setExpensePaymentFilter}
          fetchExpenses={fetchExpenses}
          openExpenseDialog={openExpenseDialog}
          handleDeleteExpense={handleDeleteExpense}
          setShowExpenseDownloadDialog={setShowExpenseDownloadDialog}
          incomeInvoices={incomeInvoices}
          openIncomeDialog={openIncomeDialog}
          handleDeleteIncome={handleDeleteIncome}
          setShowIncomeDownloadDialog={setShowIncomeDownloadDialog}
          language={language}
          t={t}
        />
      )}

      {/* Expense Dialog */}
      <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingExpense ? t.editExpense : t.newExpense}</DialogTitle>
            <DialogDescription className="text-xs text-red-500">* {t.requiredFields}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            {/* Nº Factura */}
            <div data-expense-field-error={expenseFormErrors.invoice_number ? 'true' : 'false'}>
              <Label className="text-red-600">{t.invoiceNumber} *</Label>
              <Input 
                value={expenseForm.invoice_number}
                onChange={(e) => { setExpenseForm({...expenseForm, invoice_number: e.target.value}); if (expenseFormErrors.invoice_number) setExpenseFormErrors(p => ({ ...p, invoice_number: undefined })); }}
                placeholder="Ej: F-2024-001"
                data-testid="expense-invoice-number"
                className={expenseFormErrors.invoice_number ? 'border-red-500 ring-1 ring-red-300' : ''}
              />
              {expenseFormErrors.invoice_number && (
                <p className="text-xs text-red-600 mt-1">{expenseFormErrors.invoice_number}</p>
              )}
            </div>
            
            {/* Pedidos Vinculados (multi-line table) */}
            <div className="col-span-2 border rounded-md p-3 bg-slate-50/50" data-testid="expense-order-lines">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">
                  {language === 'es' ? 'Pedidos Vinculados' : 'Linked Orders'}
                  <span className="text-xs text-muted-foreground ml-2 font-normal">
                    {language === 'es' ? '(opcional - factura puede no llevar pedidos)' : '(optional)'}
                  </span>
                </Label>
                <Popover open={orderSelectOpen} onOpenChange={setOrderSelectOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="expense-add-order-line">
                      + {language === 'es' ? 'Añadir pedido' : 'Add order'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[340px] p-0" align="end">
                    <Command
                      filter={(value, search) => {
                        if (!search) return 1;
                        const v = (value || '').toLowerCase();
                        const s = search.toLowerCase().trim();
                        if (/^\d+$/.test(s)) {
                          const m = v.match(/^#(\d+)/);
                          return m && m[1].startsWith(s) ? 1 : 0;
                        }
                        return v.includes(s) ? 1 : 0;
                      }}
                    >
                      <CommandInput placeholder={t.selectOrder} />
                      <CommandList className="max-h-72">
                        <CommandEmpty>{t.noOrderFound}</CommandEmpty>
                        <CommandGroup>
                          {allOrdersForPicker
                            .filter(o => !expenseForm.order_lines.some(l => l.order_id === o.id))
                            .map((order) => {
                              const lockedBy = lockedOrderIdToExpense[order.id];
                              // When editing, ignore lock if it's this same expense
                              const isLockedByOther = lockedBy && (!editingExpense || lockedBy.expenseId !== editingExpense.id);
                              return (
                                <CommandItem
                                  key={order.id}
                                  value={`#${order.order_number}|${order.product_url || ''}|${order.customer_name || ''}`}
                                  onSelect={() => {
                                    if (isLockedByOther) {
                                      toast.error(
                                        language === 'es'
                                          ? `Pedido ya vinculado al gasto ${lockedBy.invoiceNumber}`
                                          : `Order already linked to expense ${lockedBy.invoiceNumber}`
                                      );
                                      return;
                                    }
                                    handleAddOrderLineForExpense(order);
                                  }}
                                  className={isLockedByOther ? 'opacity-50 cursor-not-allowed' : ''}
                                  data-testid={`expense-picker-order-${order.order_number}`}
                                  title={isLockedByOther ? (language === 'es' ? `Ya vinculado al gasto ${lockedBy.invoiceNumber}` : `Already linked to expense ${lockedBy.invoiceNumber}`) : undefined}
                                >
                                  <Plus className="mr-2 h-4 w-4 opacity-50" />
                                  <div className="flex flex-col flex-1">
                                    <span className="font-medium">#{order.order_number} - {order.product_url}</span>
                                    <span className="text-xs text-muted-foreground">
                                      PVP: €{order.pvp} | PC: €{order.pc}
                                    </span>
                                  </div>
                                  {isLockedByOther && (
                                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                                      {language === 'es' ? `→ ${lockedBy.invoiceNumber}` : `→ ${lockedBy.invoiceNumber}`}
                                    </span>
                                  )}
                                </CommandItem>
                              );
                            })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {expenseForm.order_lines.length === 0 ? (
                <div className="text-xs text-muted-foreground italic py-2 text-center">
                  {language === 'es' ? 'Sin pedidos vinculados (factura general)' : 'No linked orders (general invoice)'}
                </div>
              ) : (
                (() => {
                  const sumPvp = expenseForm.order_lines.reduce((s, l) => s + (parseFloat(l.pvp) || 0), 0);
                  const sumPc = expenseForm.order_lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
                  const totalBenefit = sumPvp - sumPc;
                  return (
                    <>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-muted-foreground">
                            <th className="pb-1">{language === 'es' ? 'Pedido' : 'Order'}</th>
                            <th className="pb-1 w-24 text-right">{language === 'es' ? 'P. Venta' : 'Sale'}</th>
                            <th className="pb-1 w-24 text-right">{language === 'es' ? 'P. Compra' : 'Cost'}</th>
                            <th className="pb-1 w-32 text-right">{language === 'es' ? 'Importe' : 'Amount'}</th>
                            <th className="pb-1 w-24 text-right">{language === 'es' ? 'Beneficio' : 'Benefit'}</th>
                            <th className="pb-1 w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {expenseForm.order_lines.map((line) => {
                            const order = allOrdersForPicker.find(o => o.id === line.order_id);
                            const benefit = (parseFloat(line.pvp) || 0) - (parseFloat(line.amount) || 0);
                            return (
                              <tr key={line.order_id} className="border-t" data-testid={`expense-line-${line.order_number}`}>
                                <td className="py-1.5">
                                  <div className="font-medium">#{line.order_number}</div>
                                  {order?.product_url && (
                                    <div className="text-xs text-muted-foreground truncate max-w-[200px]">{order.product_url}</div>
                                  )}
                                </td>
                                <td className="py-1.5 text-right text-green-600">€{(parseFloat(line.pvp) || 0).toFixed(2)}</td>
                                <td className="py-1.5 text-right text-red-600">€{(parseFloat(line.pc) || 0).toFixed(2)}</td>
                                <td className="py-1.5 text-right">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={line.amount}
                                    onChange={(e) => handleUpdateOrderLineAmount(line.order_id, e.target.value)}
                                    className="h-7 text-right text-sm"
                                    data-testid={`expense-line-amount-${line.order_number}`}
                                  />
                                </td>
                                <td
                                  className={`py-1.5 text-right font-semibold ${benefit < 0 ? 'text-red-600' : 'text-emerald-600'}`}
                                  data-testid={`expense-line-benefit-${line.order_number}`}
                                >
                                  €{benefit.toFixed(2)}
                                </td>
                                <td className="py-1.5 text-right">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                    onClick={() => handleRemoveOrderLineForExpense(line.order_id)}
                                    data-testid={`expense-line-remove-${line.order_number}`}
                                  >
                                    ×
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="border-t font-semibold">
                            <td className="pt-2 text-right text-xs text-muted-foreground">
                              {language === 'es' ? 'Totales:' : 'Totals:'}
                            </td>
                            <td className="pt-2 text-right text-green-700">€{sumPvp.toFixed(2)}</td>
                            <td></td>
                            <td className="pt-2 text-right">€{sumPc.toFixed(2)}</td>
                            <td
                              className={`pt-2 text-right ${totalBenefit < 0 ? 'text-red-600' : 'text-emerald-700'}`}
                              data-testid="expense-total-benefit"
                            >
                              €{totalBenefit.toFixed(2)}
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                      {totalBenefit < 0 && (
                        <div
                          className="mt-2 flex items-start gap-2 p-2.5 rounded-md border border-red-300 bg-red-50 text-red-700 text-sm"
                          data-testid="expense-negative-benefit-warning"
                        >
                          <span className="font-bold leading-tight">⚠</span>
                          <span>
                            {language === 'es'
                              ? `Beneficio negativo: estás pagando €${Math.abs(totalBenefit).toFixed(2)} más de lo que cobras al cliente.`
                              : `Negative benefit: you're paying €${Math.abs(totalBenefit).toFixed(2)} more than you charge the client.`}
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </div>
            
            {/* Fecha Factura Gasto */}
            <div>
              <Label>{t.expenseInvoiceDate}</Label>
              <Input 
                type="date"
                value={expenseForm.invoice_date}
                onChange={(e) => setExpenseForm({...expenseForm, invoice_date: e.target.value})}
              />
            </div>
            
            {/* Fecha de Registro (editable) */}
            <div>
              <Label>{t.createdDate}</Label>
              <Input 
                type="date"
                value={expenseForm.created_date || ''}
                onChange={(e) => setExpenseForm({...expenseForm, created_date: e.target.value})}
                data-testid="expense-created-date-input"
              />
            </div>
            
            {/* Fecha de Pago (editable) */}
            <div>
              <Label className="flex items-center gap-2">
                {language === 'es' ? 'Fecha de Pago' : 'Payment Date'}
                {(expenseForm.status === 'Pagada' || expenseForm.status === 'Paid') && !expenseForm.payment_date && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                    {language === 'es' ? 'Pendiente registrar' : 'Pending to register'}
                  </span>
                )}
              </Label>
              <Input 
                type="date"
                value={expenseForm.payment_date || ''}
                onChange={(e) => setExpenseForm({...expenseForm, payment_date: e.target.value})}
                data-testid="expense-payment-date-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                {language === 'es' ? 'Fecha en que pagaste al proveedor (opcional)' : 'Date you paid the provider (optional)'}
              </p>
            </div>
            
            {/* Proveedor */}
            <div data-expense-field-error={expenseFormErrors.provider ? 'true' : 'false'}>
              <Label className="text-red-600">{t.provider} *</Label>
              <Input 
                value={expenseForm.provider}
                onChange={(e) => { setExpenseForm({...expenseForm, provider: e.target.value}); if (expenseFormErrors.provider) setExpenseFormErrors(p => ({ ...p, provider: undefined })); }}
                placeholder="Nombre del proveedor"
                className={expenseFormErrors.provider ? 'border-red-500 ring-1 ring-red-300' : ''}
              />
              {expenseFormErrors.provider && (
                <p className="text-xs text-red-600 mt-1">{expenseFormErrors.provider}</p>
              )}
            </div>
            
            {/* PVP sin IVA */}
            <div data-expense-field-error={expenseFormErrors.base_amount ? 'true' : 'false'}>
              <Label className="text-red-600">{t.baseAmount} (€) *</Label>
              <Input 
                type="number"
                value={expenseForm.base_amount}
                onChange={(e) => {
                  const base = parseFloat(e.target.value) || 0;
                  const total = base + expenseForm.iva - expenseForm.irpf;
                  setExpenseForm({...expenseForm, base_amount: base, total: parseFloat(total.toFixed(2))});
                  if (expenseFormErrors.base_amount && base > 0) setExpenseFormErrors(p => ({ ...p, base_amount: undefined }));
                }}
                className={`bg-orange-50 ${expenseFormErrors.base_amount ? 'border-red-500 ring-1 ring-red-300' : ''}`}
              />
              {expenseFormErrors.base_amount && (
                <p className="text-xs text-red-600 mt-1">{expenseFormErrors.base_amount}</p>
              )}
            </div>
            
            {/* IVA (valor en euros, manual) */}
            <div>
              <Label>{t.iva} (€)</Label>
              <Input 
                type="number"
                value={expenseForm.iva}
                onChange={(e) => {
                  const iva = parseFloat(e.target.value) || 0;
                  const total = expenseForm.base_amount + iva - expenseForm.irpf;
                  setExpenseForm({...expenseForm, iva: iva, total: parseFloat(total.toFixed(2))});
                }}
              />
            </div>
            
            {/* IRPF (valor en euros, manual) */}
            <div>
              <Label>{t.irpf} (€)</Label>
              <Input 
                type="number"
                value={expenseForm.irpf}
                onChange={(e) => {
                  const irpf = parseFloat(e.target.value) || 0;
                  const total = expenseForm.base_amount + expenseForm.iva - irpf;
                  setExpenseForm({...expenseForm, irpf: irpf, total: parseFloat(total.toFixed(2))});
                }}
              />
            </div>
            
            {/* Total (calculado automáticamente) */}
            <div>
              <Label>{t.total} (€)</Label>
              <Input 
                type="number"
                value={expenseForm.total}
                disabled
                className="bg-green-50 font-semibold cursor-not-allowed"
              />
            </div>
            
            {/* Estado */}
            <div>
              <Label className="text-red-600">{t.expenseStatus} *</Label>
              <Select value={expenseForm.status} onValueChange={(v) => setExpenseForm({...expenseForm, status: v})}>
                <SelectTrigger className={!expenseForm.status ? 'border-red-300' : ''}>
                  <SelectValue placeholder={t.expenseStatus} />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_STATUSES.map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Forma de Pago */}
            <div>
              <Label>{t.paymentMethod}</Label>
              <Select value={expenseForm.payment_method} onValueChange={(v) => setExpenseForm({...expenseForm, payment_method: v})}>
                <SelectTrigger>
                  <SelectValue placeholder={t.paymentMethod} />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(pm => (
                    <SelectItem key={pm} value={pm}>{pm}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Notas */}
            <div className="col-span-2">
              <Label>{t.notes}</Label>
              <Textarea 
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm({...expenseForm, notes: e.target.value})}
                rows={2}
              />
            </div>
            
            {/* Archivos */}
            <div className="col-span-2">
              <Label>{language === 'es' ? 'Facturas Adjuntas' : 'Attached Invoices'}</Label>
              <div className="mt-2 space-y-2">
                {/* Archivos existentes */}
                {editingExpense && expenseFiles.length > 0 && (
                  <div className="space-y-1">
                    {expenseFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-md">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-blue-500" />
                          <span className="text-sm">{file.original_name}</span>
                          <span className="text-xs text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span>
                        </div>
                        <div className="flex gap-1">
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleDownloadExpenseFile(editingExpense.id, file.filename, file.original_name)}>
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleDeleteExpenseFile(editingExpense.id, file.filename)} className="text-red-500">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Archivos pendientes */}
                {!editingExpense && pendingExpenseFiles.length > 0 && (
                  <div className="space-y-1">
                    {pendingExpenseFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-yellow-600" />
                          <span className="text-sm">{file.original_name}</span>
                          <Badge variant="outline" className="text-yellow-600">{language === 'es' ? 'Pendiente' : 'Pending'}</Badge>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => handleRemovePendingExpenseFile(index)} className="text-red-500">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Botón subir */}
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={expenseFileInputRef}
                    onChange={handleExpenseFileSelect}
                    accept=".doc,.docx,.pdf,.txt,.xls,.xlsx,.jpg,.jpeg,.png"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => expenseFileInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Subir Factura' : 'Upload Invoice'}
                  </Button>
                  <span className="text-xs text-muted-foreground">.pdf, .doc, .jpg, .png...</span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpenseDialogOpen(false)}>{t.cancel}</Button>
            <Button onClick={handleSaveExpense} data-testid="save-expense-btn">{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Dialog */}
      <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingOrder ? t.editOrder : t.newOrder}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            {/* 1. Cliente */}
            <div>
              <Label>{t.customer}</Label>
              <Popover open={customerOpen} onOpenChange={setCustomerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={customerOpen}
                    className="w-full justify-between font-normal"
                    data-testid="customer-combobox"
                  >
                    {orderForm.customer_name || t.selectCustomer}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder={t.selectCustomer} />
                    <CommandList>
                      <CommandEmpty>{t.noCustomerFound}</CommandEmpty>
                      <CommandGroup>
                        {customers.map((customer) => (
                          <CommandItem
                            key={customer.id}
                            value={customer.name}
                            onSelect={() => handleCustomerSelect(customer)}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                orderForm.customer_id === customer.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div className="flex flex-col">
                              <span>{customer.name}</span>
                              <span className="text-xs text-muted-foreground">{customer.email}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* 2. Email Cliente */}
            <div>
              <Label>{t.email} Cliente</Label>
              <Input 
                type="email" 
                value={orderForm.customer_email} 
                onChange={(e) => setOrderForm({...orderForm, customer_email: e.target.value})} 
                data-testid="customer-email-input"
              />
            </div>
            
            {/* 2.5 Nº P Exter */}
            <div>
              <Label>{t.externalOrderNumber}</Label>
              <Input 
                value={orderForm.external_order_number} 
                onChange={(e) => setOrderForm({...orderForm, external_order_number: e.target.value})}
                placeholder="Ej: EXT-2024-001"
                data-testid="external-order-number-input"
              />
            </div>
            
            {/* 3. Producto */}
            <div>
              <Label>{t.product}</Label>
              <Popover open={productOpen} onOpenChange={setProductOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={productOpen}
                    className="w-full justify-between font-normal"
                    data-testid="product-combobox"
                  >
                    {orderForm.product_url || t.selectProduct}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[350px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput 
                      placeholder={t.selectProduct} 
                      onValueChange={(value) => {
                        setProductSearch(value);
                        searchProducts(value);
                      }}
                    />
                    <CommandList>
                      {productSearchLoading ? (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      ) : products.length === 0 ? (
                        <CommandEmpty>{t.noProductFound}</CommandEmpty>
                      ) : (
                        <CommandGroup>
                          {products.map((product) => (
                            <CommandItem
                              key={product.id}
                              value={product.website}
                              onSelect={() => handleProductSelect(product)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  orderForm.product_id === product.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col gap-0.5">
                                <span className="font-medium">{product.website}</span>
                                <span className="text-xs text-muted-foreground">
                                  DR: {product.dr} | PVP: €{product.pvp} | PC: €{product.pc}
                                </span>
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {product.link_type && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                                      {product.link_type}
                                    </span>
                                  )}
                                  {(product.casino || product.cbd || product.gambling || product.cryptocurrency || product.sex || product.erotic || product.vpn || product.tarot || product.porn || product.cannabis || (product.premium_sale_price || 0) > 0) ? (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">
                                      {language === 'es' ? 'Sensible: Sí' : 'Sensitive: Yes'}
                                    </span>
                                  ) : (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                                      {language === 'es' ? 'Sensible: No' : 'Sensitive: No'}
                                    </span>
                                  )}
                                  {isAdLabelYes(product.ad_label) ? (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800">
                                      {language === 'es' ? 'Etiqueta: Sí' : 'Tag: Yes'}
                                    </span>
                                  ) : (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                                      {language === 'es' ? 'Etiqueta: No' : 'Tag: No'}
                                    </span>
                                  )}
                                  {['casino','gambling','cbd','cannabis','cryptocurrency','sex','erotic','porn','vpn','tarot'].filter(k => !!product[k]).map(tk => (
                                    <span key={tk} className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                                      {getSensitiveTopicLabel(tk, language)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedProduct && (
                <div
                  className="mt-2 p-2.5 rounded-md border border-slate-200 bg-slate-50 flex flex-wrap gap-2 text-xs"
                  data-testid="product-info-badges"
                >
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">{language === 'es' ? 'Tipo de enlace:' : 'Link type:'}</span>
                    <span
                      className="font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700"
                      data-testid="product-info-link-type"
                    >
                      {selectedProduct.link_type || (language === 'es' ? 'No especificado' : 'Not set')}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">{language === 'es' ? 'Acepta sensibles:' : 'Accepts sensitive:'}</span>
                    {(selectedProduct.casino || selectedProduct.cbd || selectedProduct.gambling || selectedProduct.cryptocurrency || selectedProduct.sex || selectedProduct.erotic || selectedProduct.vpn || selectedProduct.tarot || selectedProduct.porn || selectedProduct.cannabis || (selectedProduct.premium_sale_price || 0) > 0) ? (
                      <span
                        className="font-medium px-1.5 py-0.5 rounded bg-orange-100 text-orange-700"
                        data-testid="product-info-sensitive"
                      >
                        {language === 'es' ? 'Sí' : 'Yes'}
                      </span>
                    ) : (
                      <span
                        className="font-medium px-1.5 py-0.5 rounded bg-gray-200 text-gray-700"
                        data-testid="product-info-sensitive"
                      >
                        {language === 'es' ? 'No' : 'No'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">{language === 'es' ? 'Lleva etiqueta:' : 'Has tag:'}</span>
                    {isAdLabelYes(selectedProduct.ad_label) ? (
                      <span
                        className="font-medium px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800"
                        data-testid="product-info-ad-label"
                        title={selectedProduct.ad_label}
                      >
                        {language === 'es' ? `Sí (${selectedProduct.ad_label})` : `Yes (${selectedProduct.ad_label})`}
                      </span>
                    ) : (
                      <span
                        className="font-medium px-1.5 py-0.5 rounded bg-gray-200 text-gray-700"
                        data-testid="product-info-ad-label"
                      >
                        {language === 'es' ? 'No' : 'No'}
                      </span>
                    )}
                  </div>
                  {(() => {
                    const topics = ['casino','gambling','cbd','cannabis','cryptocurrency','sex','erotic','porn','vpn','tarot'].filter(k => !!selectedProduct[k]);
                    if (topics.length === 0) return null;
                    return (
                      <div className="basis-full flex items-center flex-wrap gap-1 pt-1 border-t border-slate-200" data-testid="product-info-sensitive-topics">
                        <span className="text-muted-foreground">{language === 'es' ? 'Temáticas sensibles:' : 'Sensitive topics:'}</span>
                        {topics.map(tk => (
                          <span key={tk} className="font-medium px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                            {getSensitiveTopicLabel(tk, language)}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            
            {/* 4. Email Web */}
            <div>
              <Label>{t.webEmail}</Label>
              <Input 
                type="email" 
                value={orderForm.web_email} 
                onChange={(e) => setOrderForm({...orderForm, web_email: e.target.value})}
                placeholder="Auto-rellenado"
                data-testid="web-email-input"
              />
            </div>
            
            {/* 5. Tipo de Precio */}
            <div>
              <Label>{t.priceType}</Label>
              <Select value={orderForm.price_type} onValueChange={(v) => setOrderForm({...orderForm, price_type: v})}>
                <SelectTrigger data-testid="price-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">{t.normal}</SelectItem>
                  <SelectItem value="sensible">{t.sensible}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* 6. Precio Compra Normal (PC) */}
            <div>
              <Label>{t.purchasePriceLabel} ({t.pc}) €</Label>
              <Input 
                type="number" 
                value={orderForm.pc === 0 ? '' : orderForm.pc} 
                onChange={(e) => setOrderForm({...orderForm, pc: e.target.value === '' ? 0 : parseFloat(e.target.value)})}
                onFocus={(e) => { if (orderForm.pc === 0) setOrderForm({...orderForm, pc: ''}); }}
                onBlur={(e) => { if (e.target.value === '') setOrderForm({...orderForm, pc: 0}); }}
                className="bg-red-50"
                data-testid="pc-input"
              />
            </div>
            
            {/* 7. Precio Venta Normal (PVP) */}
            <div>
              <Label>{t.salePriceLabel} ({t.pvp}) €</Label>
              <Input 
                type="number" 
                value={orderForm.pvp === 0 ? '' : orderForm.pvp} 
                onChange={(e) => setOrderForm({...orderForm, pvp: e.target.value === '' ? 0 : parseFloat(e.target.value)})}
                onFocus={(e) => { if (orderForm.pvp === 0) setOrderForm({...orderForm, pvp: ''}); }}
                onBlur={(e) => { if (e.target.value === '') setOrderForm({...orderForm, pvp: 0}); }}
                className="bg-green-50"
                data-testid="pvp-input"
              />
            </div>

            {/* 8. Precio Compra Sensible */}
            <div>
              <Label>{language === 'es' ? 'Compra Sensible' : 'Sensitive Purchase'} €</Label>
              <Input 
                type="number" 
                value={orderForm.premium_pc === 0 ? '' : orderForm.premium_pc} 
                onChange={(e) => setOrderForm({...orderForm, premium_pc: e.target.value === '' ? 0 : parseFloat(e.target.value)})}
                onFocus={(e) => { if (orderForm.premium_pc === 0) setOrderForm({...orderForm, premium_pc: ''}); }}
                onBlur={(e) => { if (e.target.value === '') setOrderForm({...orderForm, premium_pc: 0}); }}
                className="bg-orange-50"
                data-testid="premium-pc-input"
              />
            </div>
            
            {/* 9. Precio Venta Sensible */}
            <div>
              <Label>{language === 'es' ? 'Venta Sensible' : 'Sensitive Sale'} €</Label>
              <Input 
                type="number" 
                value={orderForm.premium_pvp === 0 ? '' : orderForm.premium_pvp} 
                onChange={(e) => setOrderForm({...orderForm, premium_pvp: e.target.value === '' ? 0 : parseFloat(e.target.value)})}
                onFocus={(e) => { if (orderForm.premium_pvp === 0) setOrderForm({...orderForm, premium_pvp: ''}); }}
                onBlur={(e) => { if (e.target.value === '') setOrderForm({...orderForm, premium_pvp: 0}); }}
                className="bg-purple-50"
                data-testid="premium-pvp-input"
              />
            </div>
            
            {/* 10. Estado Pedido */}
            <div>
              <Label>{t.status}</Label>
              <Select value={orderForm.status} onValueChange={(v) => setOrderForm({...orderForm, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ORDER_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            
            {/* URL Publicada - visible y obligatoria cuando estado es Publicada */}
            <div className={orderForm.status === 'Publicada' ? 'col-span-2' : ''}>
              <Label className={orderForm.status === 'Publicada' && !orderForm.published_url ? 'text-red-600' : ''}>
                {language === 'es' ? 'URL Publicada' : 'Published URL'} {orderForm.status === 'Publicada' && <span className="text-red-600">*</span>}
              </Label>
              <Input 
                value={orderForm.published_url} 
                onChange={(e) => setOrderForm({...orderForm, published_url: e.target.value})}
                placeholder="https://..."
                className={cn(
                  orderForm.status === 'Publicada' && !orderForm.published_url && "border-red-500",
                  orderForm.status === 'Publicada' && "bg-green-50"
                )}
              />
              {orderForm.status === 'Publicada' && !orderForm.published_url && (
                <p className="text-xs text-red-500 mt-1">
                  {language === 'es' ? 'Obligatorio cuando el estado es Publicada' : 'Required when status is Published'}
                </p>
              )}
            </div>
            
            {/* 9. Redactor */}
            <div>
              <Label className={orderForm.status === 'Redactor' && !orderForm.writer_id ? 'text-red-600' : ''}>
                {t.writer} {orderForm.status === 'Redactor' && <span className="text-red-600">*</span>}
              </Label>
              <Popover open={writerOpen} onOpenChange={setWriterOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={writerOpen}
                    className={cn(
                      "w-full justify-between font-normal",
                      orderForm.status === 'Redactor' && !orderForm.writer_id && "border-red-500"
                    )}
                    data-testid="writer-combobox"
                  >
                    {orderForm.writer || t.selectWriter}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[250px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder={t.selectWriter} />
                    <CommandList>
                      <CommandEmpty>{t.noWriterFound}</CommandEmpty>
                      <CommandGroup>
                        {writers.filter(w => w.active).map((writer) => (
                          <CommandItem
                            key={writer.id}
                            value={writer.name}
                            onSelect={() => handleWriterSelect(writer)}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                orderForm.writer_id === writer.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div className="flex flex-col">
                              <span>{writer.name}</span>
                              <span className="text-xs text-muted-foreground">{writer.email}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* 10. Nº Factura Ingreso */}
            <div>
              <Label>{t.invoiceIncome}</Label>
              <Input 
                value={orderForm.invoice_income_number} 
                onChange={(e) => setOrderForm({...orderForm, invoice_income_number: e.target.value})}
                placeholder="Ej: F-2024-001"
                data-testid="invoice-income-input"
              />
            </div>
            
            {/* 11. Nº Factura Gasto + upload PDF */}
            <div>
              <Label>{t.invoiceExpense}</Label>
              <div className="flex gap-1 items-center">
                <Input 
                  value={orderForm.invoice_expense_number} 
                  onChange={(e) => setOrderForm({...orderForm, invoice_expense_number: e.target.value})}
                  placeholder="Ej: G-2024-001"
                  data-testid="invoice-expense-input"
                  className="flex-1"
                />
                {editingOrder && (
                  <>
                    <input
                      ref={orderExpenseFileInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      style={{ display: 'none' }}
                      data-testid="order-expense-file-input"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        try {
                          const fd = new FormData();
                          fd.append('file', f);
                          const inv = orderForm.invoice_expense_number || '';
                          const url = `${API}/admin/orders/${editingOrder.id}/upload-expense-invoice` + (inv ? `?invoice_number=${encodeURIComponent(inv)}` : '');
                          const resp = await axios.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                          setOrderExpenseFile({ expense_id: resp.data.expense_id, file: { stored_name: resp.data.stored_name, original_name: resp.data.original_name } });
                          toast.success(language === 'es' ? 'Factura subida' : 'Invoice uploaded');
                        } catch (err) {
                          toast.error(err.response?.data?.detail || (language === 'es' ? 'Error al subir' : 'Upload error'));
                        } finally {
                          if (orderExpenseFileInputRef.current) orderExpenseFileInputRef.current.value = '';
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      title={language === 'es' ? 'Subir factura PDF' : 'Upload invoice PDF'}
                      onClick={() => orderExpenseFileInputRef.current?.click()}
                      data-testid="order-expense-upload-btn"
                    >
                      <Upload className="h-3.5 w-3.5" />
                    </Button>
                    {orderExpenseFile?.file?.stored_name && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        title={orderExpenseFile.file.original_name}
                        className="text-blue-600 hover:bg-blue-50 px-2 h-9"
                        data-testid="order-expense-view-pdf"
                        onClick={async () => {
                          try {
                            const url = `${API}/admin/expenses/${orderExpenseFile.expense_id}/files/${orderExpenseFile.file.stored_name}`;
                            const resp = await axios.get(url, { responseType: 'blob' });
                            const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: resp.headers['content-type'] || 'application/octet-stream' }));
                            const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
                            if (!win) {
                              const a = document.createElement('a');
                              a.href = blobUrl;
                              a.download = orderExpenseFile.file.original_name || 'factura';
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                            }
                            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
                          } catch {
                            toast.error(language === 'es' ? 'Error al abrir factura' : 'Error opening invoice');
                          }
                        }}
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                    )}
                  </>
                )}
              </div>
              {!editingOrder && (
                <p className="text-xs text-muted-foreground mt-1">
                  {language === 'es' ? 'Guarda primero el pedido para poder subir el PDF' : 'Save order first to upload PDF'}
                </p>
              )}
            </div>
            
            {/* 12. Estado Pago */}
            <div>
              <Label>{t.paymentStatus}</Label>
              <Select value={orderForm.payment_status} onValueChange={(v) => setOrderForm({...orderForm, payment_status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            
            {/* Información adicional */}
            <div className="col-span-2">
              <Label>{t.information}</Label>
              <Textarea value={orderForm.information} onChange={(e) => setOrderForm({...orderForm, information: e.target.value})} rows={2} />
            </div>

            {/* Sección Redactor - URLs y Anchor Texts */}
            <div className="col-span-2 border-t pt-4 mt-2">
              <h4 className="font-semibold text-indigo-700 mb-3 flex items-center gap-2">
                <Send className="h-4 w-4" />
                {language === 'es' ? 'Datos para el Redactor' : 'Writer Data'}
              </h4>
              
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <Label>
                    URL 1
                    {orderForm.status === 'Redactor' && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  <Input 
                    value={orderForm.url_1} 
                    onChange={(e) => setOrderForm({...orderForm, url_1: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label>
                    Anchor Text 1
                    {orderForm.status === 'Redactor' && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  <Input 
                    value={orderForm.anchor_text_1} 
                    onChange={(e) => setOrderForm({...orderForm, anchor_text_1: e.target.value})}
                    placeholder={language === 'es' ? 'Texto ancla' : 'Anchor text'}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <Label>URL 2</Label>
                  <Input 
                    value={orderForm.url_2} 
                    onChange={(e) => setOrderForm({...orderForm, url_2: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label>Anchor Text 2</Label>
                  <Input 
                    value={orderForm.anchor_text_2} 
                    onChange={(e) => setOrderForm({...orderForm, anchor_text_2: e.target.value})}
                    placeholder={language === 'es' ? 'Texto ancla' : 'Anchor text'}
                  />
                </div>
              </div>
              
              <div>
                <Label>{language === 'es' ? 'Instrucciones para el Redactor' : 'Writer Instructions'}</Label>
                <Textarea 
                  value={orderForm.writer_instructions} 
                  onChange={(e) => setOrderForm({...orderForm, writer_instructions: e.target.value})}
                  rows={3}
                  placeholder={language === 'es' ? 'Instrucciones específicas para el redactor...' : 'Specific instructions for the writer...'}
                />
              </div>
              
              {/* Número de palabras e Idioma - obligatorios para estado Redactor */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>
                    {language === 'es' ? 'Número de Palabras' : 'Word Count'}
                    {orderForm.status === 'Redactor' && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  <Select 
                    value={orderForm.word_count} 
                    onValueChange={(value) => setOrderForm({...orderForm, word_count: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={language === 'es' ? 'Seleccionar...' : 'Select...'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="600">600 palabras</SelectItem>
                      <SelectItem value="700">700 palabras</SelectItem>
                      <SelectItem value="800">800 palabras</SelectItem>
                      <SelectItem value="900">900 palabras</SelectItem>
                      <SelectItem value="1000">1000 palabras</SelectItem>
                      <SelectItem value="1100">1100 palabras</SelectItem>
                      <SelectItem value="1200">1200 palabras</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>
                    {language === 'es' ? 'Idioma del Artículo' : 'Article Language'}
                    {orderForm.status === 'Redactor' && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  <Select 
                    value={orderForm.article_language} 
                    onValueChange={(value) => setOrderForm({...orderForm, article_language: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={language === 'es' ? 'Seleccionar idioma...' : 'Select language...'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Español">Español</SelectItem>
                      <SelectItem value="Inglés">Inglés</SelectItem>
                      <SelectItem value="Francés">Francés</SelectItem>
                      <SelectItem value="Alemán">Alemán</SelectItem>
                      <SelectItem value="Italiano">Italiano</SelectItem>
                      <SelectItem value="Portugués">Portugués</SelectItem>
                      <SelectItem value="Holandés">Holandés</SelectItem>
                      <SelectItem value="Polaco">Polaco</SelectItem>
                      <SelectItem value="Turco">Turco</SelectItem>
                      <SelectItem value="Japonés">Japonés</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Indicador de envío al redactor - solo informativo */}
              {editingOrder && editingOrder.sent_to_writer && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-sm text-green-600 flex items-center gap-1">
                    <Check className="h-4 w-4" />
                    {language === 'es' ? 'Enviado al redactor' : 'Sent to writer'}
                  </span>
                </div>
              )}
            </div>
            
            {/* 13. Archivos adjuntos - Siempre visible */}
            <div className="col-span-2">
              <Label>{language === 'es' ? 'Archivos Adjuntos' : 'Attached Files'}</Label>
              <div className="mt-2 space-y-2">
                {/* Lista de archivos ya subidos (solo cuando se edita) */}
                {editingOrder && orderFiles.length > 0 && (
                  <div className="space-y-1">
                    {orderFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-md">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-blue-500" />
                          <span className="text-sm">{file.original_name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadFile(editingOrder.id, file.filename, file.original_name)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteFile(editingOrder.id, file.filename)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Lista de archivos pendientes (cuando se crea nuevo pedido) */}
                {!editingOrder && pendingFiles.length > 0 && (
                  <div className="space-y-1">
                    {pendingFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-yellow-600" />
                          <span className="text-sm">{file.original_name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                          <Badge variant="outline" className="text-yellow-600 border-yellow-400">
                            {language === 'es' ? 'Pendiente' : 'Pending'}
                          </Badge>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemovePendingFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Botón para subir archivo */}
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".doc,.docx,.pdf,.txt,.xls,.xlsx"
                    className="hidden"
                    data-testid="file-upload-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile}
                    data-testid="upload-file-btn"
                  >
                    {uploadingFile ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    {language === 'es' ? 'Subir Archivo' : 'Upload File'}
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {language === 'es' ? '.doc, .docx, .pdf, .txt, .xls, .xlsx' : '.doc, .docx, .pdf, .txt, .xls, .xlsx'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderDialogOpen(false)}>{t.cancel}</Button>
            <Button onClick={handleSaveOrder} data-testid="save-order-btn">{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Dialog */}
      <Dialog open={customerDialogOpen} onOpenChange={setCustomerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCustomer ? t.editCustomer : t.newCustomer}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t.name} *</Label>
                <Input value={customerForm.name} onChange={(e) => setCustomerForm({...customerForm, name: e.target.value})} />
              </div>
              <div>
                <Label>{t.company}</Label>
                <Input value={customerForm.company} onChange={(e) => setCustomerForm({...customerForm, company: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t.email} *</Label>
                <Input type="email" value={customerForm.email} onChange={(e) => setCustomerForm({...customerForm, email: e.target.value})} />
              </div>
              <div>
                <Label>{t.phone}</Label>
                <Input value={customerForm.phone} onChange={(e) => setCustomerForm({...customerForm, phone: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>CIF/NIF</Label>
                <Input 
                  value={customerForm.nif} 
                  onChange={(e) => setCustomerForm({...customerForm, nif: e.target.value})} 
                  placeholder="B12345678"
                />
              </div>
              <div>
                <Label>{language === 'es' ? 'Dirección de Facturación' : 'Billing Address'}</Label>
                <Input 
                  value={customerForm.billing_address} 
                  onChange={(e) => setCustomerForm({...customerForm, billing_address: e.target.value})} 
                  placeholder={language === 'es' ? 'Calle, Ciudad, CP' : 'Street, City, ZIP'}
                />
              </div>
            </div>
            <div>
              <Label>{t.notes}</Label>
              <Textarea value={customerForm.notes} onChange={(e) => setCustomerForm({...customerForm, notes: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomerDialogOpen(false)}>{t.cancel}</Button>
            <Button onClick={handleSaveCustomer}>{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Writer Dialog */}
      <Dialog open={writerDialogOpen} onOpenChange={setWriterDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingWriter ? t.editWriter : t.newWriter}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>{t.name}</Label>
              <Input value={writerForm.name} onChange={(e) => setWriterForm({...writerForm, name: e.target.value})} />
            </div>
            <div>
              <Label>{t.email}</Label>
              <Input type="email" value={writerForm.email} onChange={(e) => setWriterForm({...writerForm, email: e.target.value})} />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="writer-active"
                checked={writerForm.active}
                onChange={(e) => setWriterForm({...writerForm, active: e.target.checked})}
                className="h-4 w-4"
              />
              <Label htmlFor="writer-active">{t.active}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWriterDialogOpen(false)}>{t.cancel}</Button>
            <Button onClick={handleSaveWriter}>{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expense Download Dialog */}
      <Dialog open={showExpenseDownloadDialog} onOpenChange={setShowExpenseDownloadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === 'es' ? 'Descargar Gastos' : 'Download Expenses'}</DialogTitle>
            <DialogDescription>
              {language === 'es' ? 'Selecciona el rango de fechas' : 'Select date range'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Filter by which date field */}
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Filtrar por' : 'Filter by'}</Label>
              <Select value={expenseDownloadDateField} onValueChange={setExpenseDownloadDateField}>
                <SelectTrigger data-testid="expense-download-date-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoice_date">
                    {language === 'es' ? 'Fecha de Factura' : 'Invoice Date'}
                  </SelectItem>
                  <SelectItem value="created_date">
                    {language === 'es' ? 'Fecha de Registro (en CRM)' : 'Registered Date (in CRM)'}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {expenseDownloadDateField === 'invoice_date'
                  ? (language === 'es' ? 'Filtra por la fecha que pone la factura del proveedor' : 'Filters by the date on the supplier invoice')
                  : (language === 'es' ? 'Filtra por la fecha en la que registraste el gasto en el CRM' : 'Filters by the date you registered the expense in the CRM')}
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                variant={expenseDownloadPreset === 'last_month' ? 'default' : 'outline'}
                onClick={() => {
                  setExpenseDownloadPreset('last_month');
                  setExpenseDownloadDateFrom('');
                  setExpenseDownloadDateTo('');
                }}
              >
                {language === 'es' ? 'Mes Pasado' : 'Last Month'}
              </Button>
              <Button
                variant={expenseDownloadPreset === '' ? 'default' : 'outline'}
                onClick={() => setExpenseDownloadPreset('')}
              >
                {language === 'es' ? 'Personalizado' : 'Custom'}
              </Button>
            </div>
            
            {expenseDownloadPreset === '' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Desde' : 'From'}</Label>
                  <Input
                    type="date"
                    value={expenseDownloadDateFrom}
                    onChange={(e) => setExpenseDownloadDateFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Hasta' : 'To'}</Label>
                  <Input
                    type="date"
                    value={expenseDownloadDateTo}
                    onChange={(e) => setExpenseDownloadDateTo(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExpenseDownloadDialog(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleDownloadExpenses}>
              <Download className="h-4 w-4 mr-2" />
              {language === 'es' ? 'Descargar' : 'Download'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Income Dialog */}
      <Dialog open={showIncomeDialog} onOpenChange={setShowIncomeDialog}>
        <DialogContent className="max-w-6xl w-[96vw] max-h-[95vh] flex flex-col p-4">
          <DialogHeader className="pb-1">
            <DialogTitle className="text-base">
              {editingIncome
                ? (language === 'es' ? 'Editar Factura de Ingreso' : 'Edit Income Invoice')
                : (language === 'es' ? 'Nueva Factura de Ingreso' : 'New Income Invoice')
              }
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* LEFT COLUMN */}
            <div className="space-y-2 overflow-y-auto pr-1">

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t.invoiceNumber} *</Label>
                <Input
                  value={incomeForm.invoice_number}
                  onChange={(e) => setIncomeForm({...incomeForm, invoice_number: e.target.value})}
                  placeholder="INV-2024-001"
                  className="h-8"
                />
              </div>
              <div></div>
            </div>

            {/* Pedidos vinculados (multi-line) */}
            <div className="border rounded-md p-3 bg-slate-50/50" data-testid="income-order-lines">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">
                  {language === 'es' ? 'Pedidos Vinculados' : 'Linked Orders'}
                  <span className="text-xs text-muted-foreground ml-2 font-normal">
                    {language === 'es' ? '(opcional)' : '(optional)'}
                  </span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="income-add-order-line">
                      + {language === 'es' ? 'Añadir pedido' : 'Add order'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[340px] p-0" align="end">
                    <Command
                      filter={(value, search) => {
                        if (!search) return 1;
                        const v = (value || '').toLowerCase();
                        const s = search.toLowerCase().trim();
                        if (/^\d+$/.test(s)) {
                          const m = v.match(/^#(\d+)/);
                          return m && m[1].startsWith(s) ? 1 : 0;
                        }
                        return v.includes(s) ? 1 : 0;
                      }}
                    >
                      <CommandInput placeholder={t.selectOrder} />
                      <CommandList className="max-h-72">
                        <CommandEmpty>{t.noOrderFound}</CommandEmpty>
                        <CommandGroup>
                          {allOrdersForPicker
                            .filter(o => !incomeForm.order_lines.some(l => l.order_id === o.id))
                            .map((order) => (
                              <CommandItem
                                key={order.id}
                                value={`#${order.order_number}|${order.product_url || ''}|${order.customer_name || ''}`}
                                onSelect={() => handleAddOrderLineForIncome(order)}
                              >
                                <Plus className="mr-2 h-4 w-4 opacity-50" />
                                <div className="flex flex-col">
                                  <span className="font-medium">#{order.order_number} - {order.product_url}</span>
                                  <span className="text-xs text-muted-foreground">PVP: €{order.pvp}</span>
                                </div>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              {incomeForm.order_lines.length === 0 ? (
                <div className="text-xs text-muted-foreground italic py-2 text-center">
                  {language === 'es' ? 'Sin pedidos vinculados (factura general)' : 'No linked orders (general invoice)'}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="pb-1">{language === 'es' ? 'Pedido' : 'Order'}</th>
                      <th className="pb-1 w-24 text-right">PVP</th>
                      <th className="pb-1 w-32 text-right">{language === 'es' ? 'Importe' : 'Amount'}</th>
                      <th className="pb-1 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {incomeForm.order_lines.map((line) => {
                      const order = allOrdersForPicker.find(o => o.id === line.order_id);
                      return (
                        <tr key={line.order_id} className="border-t" data-testid={`income-line-${line.order_number}`}>
                          <td className="py-1.5">
                            <div className="font-medium">#{line.order_number}</div>
                            {order?.product_url && (
                              <div className="text-xs text-muted-foreground truncate max-w-[200px]">{order.product_url}</div>
                            )}
                          </td>
                          <td className="py-1.5 text-right text-green-600">€{line.pvp}</td>
                          <td className="py-1.5 text-right">
                            <Input
                              type="number"
                              step="0.01"
                              value={line.amount}
                              onChange={(e) => handleUpdateIncomeLineAmount(line.order_id, e.target.value)}
                              className="h-7 text-right text-sm"
                            />
                          </td>
                          <td className="py-1.5 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                              onClick={() => handleRemoveOrderLineForIncome(line.order_id)}
                            >
                              ×
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t font-semibold">
                      <td colSpan={2} className="pt-2 text-right text-xs text-muted-foreground">
                        {language === 'es' ? 'Suma líneas:' : 'Lines sum:'}
                      </td>
                      <td className="pt-2 text-right">
                        €{incomeForm.order_lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0).toFixed(2)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>

            </div>{/* end LEFT column */}

            {/* RIGHT COLUMN */}
            <div className="space-y-2 overflow-y-auto pr-1">

            {/* Customer selector */}
            <div>
              <Label className="text-xs">{language === 'es' ? 'Seleccionar Cliente Existente' : 'Select Existing Client'}</Label>
              <Select onValueChange={handleIncomeCustomerSelect}>
                <SelectTrigger className="h-8">
                  <SelectValue placeholder={language === 'es' ? 'Seleccionar cliente...' : 'Select client...'} />
                </SelectTrigger>
                <SelectContent>
                  {customers.length === 0 ? (
                    <SelectItem value="none" disabled>
                      {language === 'es' ? 'No hay clientes' : 'No clients'}
                    </SelectItem>
                  ) : (
                    customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.company || customer.name} {customer.nif ? `(${customer.nif})` : ''}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{language === 'es' ? 'Cliente' : 'Client'} *</Label>
                <Input
                  value={incomeForm.client_name}
                  onChange={(e) => setIncomeForm({...incomeForm, client_name: e.target.value})}
                />
              </div>
              <div>
                <Label>{language === 'es' ? 'NIF/CIF' : 'Tax ID'}</Label>
                <Input
                  value={incomeForm.client_nif}
                  onChange={(e) => setIncomeForm({...incomeForm, client_nif: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>{language === 'es' ? 'Descripción' : 'Description'}</Label>
              <Textarea
                value={incomeForm.description}
                onChange={(e) => setIncomeForm({...incomeForm, description: e.target.value})}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>{t.baseAmount} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={incomeForm.base_amount}
                  onChange={(e) => setIncomeForm({...incomeForm, base_amount: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>{t.iva} (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={incomeForm.iva}
                  onChange={(e) => setIncomeForm({...incomeForm, iva: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>{t.irpf} (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={incomeForm.irpf}
                  onChange={(e) => setIncomeForm({...incomeForm, irpf: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t.total}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={incomeForm.total}
                  readOnly
                  className="bg-gray-50"
                />
              </div>
              <div>
                <Label>{language === 'es' ? 'Fecha Factura' : 'Invoice Date'}</Label>
                <Input
                  type="date"
                  value={incomeForm.invoice_date}
                  onChange={(e) => setIncomeForm({...incomeForm, invoice_date: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  {language === 'es' ? 'Fecha de Pago' : 'Payment Date'}
                  {(incomeForm.status === 'Cobrada' || incomeForm.status === 'Paid') && !incomeForm.payment_date && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                      {language === 'es' ? 'Pendiente registrar' : 'Pending to register'}
                    </span>
                  )}
                </Label>
                <Input
                  type="date"
                  value={incomeForm.payment_date || ''}
                  onChange={(e) => setIncomeForm({...incomeForm, payment_date: e.target.value})}
                  data-testid="income-payment-date-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  {language === 'es' ? 'Fecha en que el cliente realmente pagó (opcional)' : 'Date when client actually paid (optional)'}
                </p>
              </div>
              <div></div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t.paymentMethod}</Label>
                <Select
                  value={incomeForm.payment_method}
                  onValueChange={(value) => setIncomeForm({...incomeForm, payment_method: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'es' ? 'Seleccionar método' : 'Select method'} />
                  </SelectTrigger>
                  <SelectContent>
                    {INCOME_PAYMENT_METHODS.map(method => (
                      <SelectItem key={method} value={method}>{method}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{language === 'es' ? 'Estado' : 'Status'}</Label>
                <Select
                  value={incomeForm.status}
                  onValueChange={(value) => setIncomeForm({...incomeForm, status: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INCOME_STATUSES.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>{t.notes}</Label>
              <Textarea
                value={incomeForm.notes}
                onChange={(e) => setIncomeForm({...incomeForm, notes: e.target.value})}
                rows={3}
              />
            </div>

            {/* File Upload Section */}
            <div className="border-t pt-4">
              <Label className="flex items-center gap-2 mb-3">
                <FileText className="h-4 w-4" />
                {language === 'es' ? 'Archivos Adjuntos' : 'Attached Files'}
              </Label>
              
              {/* Hidden file input */}
              <input
                type="file"
                ref={incomeFileInputRef}
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handleIncomeFileSelect}
                multiple
              />
              
              {/* Upload button */}
              <Button
                type="button"
                variant="outline"
                onClick={() => incomeFileInputRef.current?.click()}
                className="mb-3"
              >
                <Upload className="h-4 w-4 mr-2" />
                {language === 'es' ? 'Subir Factura PDF' : 'Upload Invoice PDF'}
              </Button>

              {/* Existing files (when editing) */}
              {incomeFiles.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm text-muted-foreground mb-2">
                    {language === 'es' ? 'Archivos existentes:' : 'Existing files:'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {incomeFiles.map((file) => (
                      <div key={file.filename} className="flex items-center gap-2 bg-green-50 border border-green-200 px-3 py-1.5 rounded-lg">
                        <FileText className="h-4 w-4 text-green-600" />
                        <span className="text-sm">{file.original_name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => handleDownloadIncomeFile(editingIncome.id, file.filename, file.original_name)}
                        >
                          <Download className="h-3 w-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteIncomeFile(editingIncome.id, file.filename)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pending files (to be uploaded) */}
              {pendingIncomeFiles.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {language === 'es' ? 'Archivos por subir:' : 'Files to upload:'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {pendingIncomeFiles.map((file, index) => (
                      <div key={index} className="flex items-center gap-2 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-lg">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <span className="text-sm">{file.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          onClick={() => handleRemovePendingIncomeFile(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            </div>{/* end RIGHT column */}
          </div>{/* end grid */}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIncomeDialog(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleSaveIncome}>
              {t.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Income Download Dialog */}
      <Dialog open={showIncomeDownloadDialog} onOpenChange={setShowIncomeDownloadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === 'es' ? 'Descargar Facturas de Ingresos' : 'Download Income Invoices'}</DialogTitle>
            <DialogDescription>
              {language === 'es' ? 'Selecciona el rango de fechas' : 'Select date range'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Filter by which date field */}
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Filtrar por' : 'Filter by'}</Label>
              <Select value={incomeDownloadDateField} onValueChange={setIncomeDownloadDateField}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoice_date">
                    {language === 'es' ? 'Fecha de Factura' : 'Invoice Date'}
                  </SelectItem>
                  <SelectItem value="created_date">
                    {language === 'es' ? 'Fecha de Registro (en CRM)' : 'Registered Date (in CRM)'}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {incomeDownloadDateField === 'invoice_date'
                  ? (language === 'es' ? 'Filtra por la fecha que pone la factura' : 'Filters by the date on the invoice')
                  : (language === 'es' ? 'Filtra por la fecha en la que registraste el ingreso en el CRM' : 'Filters by the date you registered the income in the CRM')}
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                variant={incomeDownloadPreset === 'last_month' ? 'default' : 'outline'}
                onClick={() => {
                  setIncomeDownloadPreset('last_month');
                  setIncomeDownloadDateFrom('');
                  setIncomeDownloadDateTo('');
                }}
              >
                {language === 'es' ? 'Mes Pasado' : 'Last Month'}
              </Button>
              <Button
                variant={incomeDownloadPreset === '' ? 'default' : 'outline'}
                onClick={() => setIncomeDownloadPreset('')}
              >
                {language === 'es' ? 'Personalizado' : 'Custom'}
              </Button>
            </div>
            
            {incomeDownloadPreset === '' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Desde' : 'From'}</Label>
                  <Input
                    type="date"
                    value={incomeDownloadDateFrom}
                    onChange={(e) => setIncomeDownloadDateFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Hasta' : 'To'}</Label>
                  <Input
                    type="date"
                    value={incomeDownloadDateTo}
                    onChange={(e) => setIncomeDownloadDateTo(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIncomeDownloadDialog(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleDownloadIncome}>
              <Download className="h-4 w-4 mr-2" />
              {language === 'es' ? 'Descargar' : 'Download'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Import Dialog */}
      <Dialog open={orderImportDialogOpen} onOpenChange={setOrderImportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{language === 'es' ? 'Importar Pedidos' : 'Import Orders'}</DialogTitle>
            <DialogDescription>
              {language === 'es' 
                ? 'Sube un archivo Excel o CSV con los pedidos'
                : 'Upload an Excel or CSV file with orders'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{language === 'es' ? 'Archivo' : 'File'}</Label>
              <Input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setOrderImportFile(e.target.files[0])}
                ref={orderImportFileRef}
              />
              {orderImportFile && (
                <p className="text-sm text-muted-foreground mt-1">{orderImportFile.name}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="replaceAllOrders"
                checked={orderImportReplaceAll}
                onChange={(e) => setOrderImportReplaceAll(e.target.checked)}
              />
              <Label htmlFor="replaceAllOrders" className="text-sm text-red-600">
                {language === 'es' 
                  ? 'Eliminar pedidos existentes antes de importar'
                  : 'Delete existing orders before importing'}
              </Label>
            </div>
            <div className="text-xs text-muted-foreground">
              <p className="font-medium mb-1">{language === 'es' ? 'Columnas soportadas:' : 'Supported columns:'}</p>
              <p>Cliente, Email Cliente, URL Producto, Estado, PVP, PC, Redactor, Fecha Pedido, URL 1, Anchor 1, URL 2, Anchor 2...</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderImportDialogOpen(false)}>
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleImportOrders} 
              disabled={!orderImportFile || orderImportLoading}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {orderImportLoading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {language === 'es' ? 'Importando...' : 'Importing...'}</>
              ) : (
                <><Upload className="h-4 w-4 mr-2" /> {language === 'es' ? 'Importar' : 'Import'}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRMTab;
